import type { Plant } from '@/types/plant';

export const PLANTS: Plant[] = [
  {
    id: 'tomato',
    name: 'Tomato',
    latinName: 'Solanum lycopersicum',
    family: 'solanaceae',
    emoji: '🍅',
    description: 'The jewel of the summer garden, tomatoes reward careful cultivation with an abundance of sweet, sun-ripened fruit. Nothing from the supermarket shelf can match a tomato picked warm from the vine.',
    difficulty: 'moderate',
    biodynamicDay: 'fruit',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [2, 3, 4],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['basil', 'carrot', 'marigold', 'parsley'],
    antagonists: ['fennel', 'brassica'],
    soilType: 'Rich, well-draining loam with plenty of organic matter',
    soilPH: { min: 6.0, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 75, plant: 60 },
    depthCm: 0.5,
    daysToGerminate: { min: 6, max: 14 },
    daysToHarvest: { min: 60, max: 85 },
    cultivation: `## Sowing\nSow seeds indoors 6–8 weeks before last frost date, 6mm deep in seed compost. Keep at 18–24°C. Once seedlings have two true leaves, prick out into individual 9cm pots.\n\n## Growing On\nGrow on in a bright, frost-free place. Pot on as needed. Harden off for 1–2 weeks before planting out after all risk of frost has passed.\n\n## Support & Training\nCordon (indeterminate) varieties need staking and regular sideshooting — pinch out the shoots that develop in the leaf axils. Bush (determinate) varieties are more compact and need less attention.\n\n## Feeding\nOnce the first truss of flowers sets, feed weekly with a high-potash organic liquid feed (comfrey tea is ideal). Consistent watering prevents blossom end rot and fruit splitting.`,
    harvesting: `Pick when fully coloured and slightly yielding to gentle pressure. In late summer, pick trusses of green tomatoes before the first frost and ripen indoors on a windowsill (not in the fridge — cold destroys flavour). Harvest regularly to encourage more fruit.`,
    storage: 'Never refrigerate ripe tomatoes — cold temperatures destroy volatile flavour compounds. Store at room temperature, stem-side down. Use within a week.',
    pests: ['Aphids', 'Whitefly', 'Red spider mite', 'Tomato moth caterpillar'],
    diseases: ['Blight (Phytophthora infestans)', 'Botrytis grey mould', 'Leaf mould', 'Mosaic virus'],
    organicTips: [
      'Spray diluted comfrey tea as a foliar feed rich in potassium',
      'Companion plant with basil — reportedly improves flavour and deters aphids',
      'Use copper tape around pots to deter slugs',
      'Introduce ladybirds and lacewings to control aphid populations',
      'Remove lower leaves to improve air circulation and reduce blight risk',
    ],
    historicalNote: 'Tomatoes were long suspected to be poisonous in Britain — being in the Nightshade family, the leaves and stems are indeed toxic. It was not until the 19th century that they became widely eaten. Before chemical fungicides were normalised in the mid-20th century, copper-based sprays (Bordeaux mixture) were the primary blight control — still permitted in organic growing.',
    nutritionFacts: 'Rich in lycopene (a powerful antioxidant), vitamins C and K, folate, and potassium. Lycopene is more bioavailable in cooked tomatoes.',
    imageUrl: '/images/plants/tomato.jpg',
    tags: ['summer', 'warm-season', 'fruit', 'greenhouse', 'container', 'popular'],
  },
  {
    id: 'carrot',
    name: 'Carrot',
    latinName: 'Daucus carota subsp. sativus',
    family: 'root',
    emoji: '🥕',
    description: 'Fresh-dug carrots from your own ground bear no resemblance to their cellophane-wrapped cousins. Sweet, earthy, and deeply satisfying to pull from the soil.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 6, 7],
      harvest: [6, 7, 8, 9, 10, 11],
    },
    companions: ['onion', 'leek', 'tomato', 'rosemary', 'sage'],
    antagonists: ['dill', 'parsnip'],
    soilType: 'Light, sandy or stony-free loam. Avoid freshly manured ground (causes forking)',
    soilPH: { min: 6.0, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 5 },
    depthCm: 1,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 70, max: 80 },
    cultivation: `## Preparing the Bed\nCarrots need deep, loose, stone-free soil. If your soil is heavy or stony, grow shorter round varieties or raise beds. Never add fresh manure before sowing — this causes roots to fork. Mix in a little sharp sand to improve drainage.\n\n## Sowing\nSow thinly direct, 1cm deep, in rows 30cm apart from March to July. Carrot seed is very fine — mix with dry sand for easier sowing. Germination can be slow and erratic. Cover with fleece to warm the soil and deter carrot fly.\n\n## Thinning\nThin in two stages to 5–8cm apart, doing so in the evening to avoid attracting carrot fly (which is drawn to the smell of crushed foliage). Remove thinnings from the plot.`,
    harvesting: `Pull when roots are of usable size — young carrots at 10–12 weeks are sweet and tender. Use a fork to loosen roots in heavy ground. Maincrop varieties can be left in the ground and harvested as needed through autumn, or lifted before hard frosts and stored.`,
    storage: 'Remove tops (which draw moisture) and store in boxes of slightly moist sand in a cool, frost-free shed. Will keep for several months.',
    pests: ['Carrot fly (Psila rosae)', 'Aphids', 'Slugs', 'Wireworm'],
    diseases: ['Carrot cavity spot', 'Violet root rot', 'Powdery mildew'],
    organicTips: [
      'Cover with fine insect-proof mesh from sowing to harvest to exclude carrot fly',
      'Erect a 60cm barrier of fleece around the bed — carrot fly flies low',
      'Companion plant with onions or leeks — the smell confuses carrot fly',
      'Sow alongside rows of strong-smelling herbs like rosemary and sage',
      'Thin in the evening, when carrot fly is less active',
    ],
    historicalNote: 'Wild carrots were white or purple — orange carrots were developed in the Netherlands in the 17th century, possibly as a tribute to William of Orange. Before the carrot fly pesticide era, companion planting with onions was the primary organic defence, a method that modern research has confirmed is partly effective.',
    imageUrl: '/images/plants/carrot.jpg',
    tags: ['root', 'cool-season', 'direct-sow', 'popular', 'winter-store'],
  },
  {
    id: 'courgette',
    name: 'Courgette',
    latinName: 'Cucurbita pepo',
    family: 'cucurbit',
    emoji: '🥒',
    description: 'Courgettes are the most generous plants in the garden — sometimes embarrassingly so. One or two plants will supply a family through summer. Pick young for the best flavour; leave them and you have a marrow.',
    difficulty: 'easy',
    biodynamicDay: 'fruit',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [6, 7, 8, 9, 10],
    },
    companions: ['bean', 'pea', 'nasturtium', 'sweetcorn'],
    antagonists: ['potato', 'fennel'],
    soilType: 'Rich, moisture-retentive but well-drained soil, ideally over a manure pit',
    soilPH: { min: 5.5, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 90, plant: 90 },
    depthCm: 2.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 50, max: 65 },
    cultivation: `## The Three Sisters\nCourgettes grow beautifully alongside sweetcorn and climbing beans in the Native American "Three Sisters" combination — the courgette's large leaves shade the ground, suppressing weeds and retaining moisture.\n\n## Sowing\nSow seeds on their edge (not flat) in individual pots of multipurpose compost in late April or May. Keep at 18°C. Transplant after all frosts have passed to a sunny, sheltered spot.\n\n## Pollination\nIn cool, wet summers, bees may not visit enough to pollinate — hand-pollinate by transferring pollen from male flowers (on a thin stalk) to female flowers (with a tiny courgette at the base) using a fine paintbrush.`,
    harvesting: `Harvest frequently, every 2–3 days in peak season, cutting with a sharp knife at 10–15cm for the best flavour and tenderness. Regular harvesting keeps the plant productive. The flowers are edible and a delicacy — harvest male flowers (they have no embryo fruit at the base) and stuff and fry them.`,
    storage: 'Use within a few days of harvest at room temperature. Do not refrigerate — cold impairs texture. Surplus courgettes can be grated and frozen for use in baking.',
    pests: ['Slugs (on young plants)', 'Aphids', 'Vine weevil', 'Red spider mite (in dry conditions)'],
    diseases: ['Powdery mildew (common in late season)', 'Cucumber mosaic virus', 'Botrytis'],
    organicTips: [
      'Grow on a mound over a pit of well-rotted manure or compost for vigorous growth',
      'Mulch heavily with straw to retain moisture and suppress weeds',
      'Hand-pollinate in poor weather to ensure fruit set',
      'Spray with diluted milk to prevent and treat powdery mildew',
      'Plant nasturtiums as a sacrifice crop to lure aphids away',
    ],
    historicalNote: 'The courgette was popularised in Britain relatively recently — post-war immigrants from Italy and France brought it to market gardens and allotments. Before this, British gardeners grew large marrows, the blandness of which gave squashes an undeserved poor reputation for generations.',
    imageUrl: '/images/plants/marrow.jpg',
    tags: ['summer', 'warm-season', 'fruit', 'prolific', 'beginner-friendly'],
  },
  {
    id: 'bean-runner',
    name: 'Runner Bean',
    latinName: 'Phaseolus coccineus',
    family: 'legume',
    emoji: '🫛',
    description: 'With their scarlet flowers and vigorous climbing habit, runner beans are as ornamental as they are productive. A classic British allotment plant that rewards little effort with enormous crops.',
    difficulty: 'easy',
    biodynamicDay: 'flower',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['sweetcorn', 'courgette', 'summer-savory', 'nasturtium'],
    antagonists: ['onion', 'fennel', 'beetroot'],
    soilType: 'Rich, deeply-dug soil with plenty of organic matter and good moisture retention',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 60, plant: 30 },
    depthCm: 5,
    daysToGerminate: { min: 7, max: 10 },
    daysToHarvest: { min: 60, max: 70 },
    cultivation: `## Preparing the Bean Trench\nIn autumn, dig a trench 30cm deep and fill with kitchen scraps, compost, or well-rotted manure through winter. By spring you'll have a perfect moisture-retaining, nutrient-rich bed.\n\n## Structure\nErect a sturdy structure of canes (2.4m minimum) in a double row or wigwam before planting. Runner beans are vigorous and heavy — the support must be strong.\n\n## Sowing\nSow in pots indoors in April/May, or direct into warm soil from late May. Sow 5cm deep, two seeds per station. Nip out the weaker seedling when established.`,
    harvesting: `Harvest when pods are 15–20cm long and still flexible, before the beans inside become visible as bulges. Pick every 2–3 days — regular harvesting is essential to maintain cropping. Old, stringy pods left on the plant signal the plant to stop producing.`,
    storage: 'Best eaten fresh. Blanch and freeze any surplus — beans freeze excellently. Or dry fully mature seeds for next year\'s seed or as haricot-type beans.',
    pests: ['Black bean aphid (blackfly)', 'Slugs', 'Bean seed fly', 'Red spider mite'],
    diseases: ['Halo blight', 'Anthracnose', 'Root rot'],
    organicTips: [
      'Rub off early blackfly colonies by hand before they establish',
      'Encourage natural predators — plant fennel or dill nearby for hoverflies',
      'Water the base of plants (not the leaves) in the evening to retain moisture',
      'Save seed from the best pods at the end of season — runner beans come true from seed',
      'Dig plant roots in at season end — they fix nitrogen in the soil',
    ],
    historicalNote: 'Runner beans were introduced to Britain from Central America in the 17th century, initially grown as ornamentals for their vivid flowers rather than for food. The practice of digging a deep autumn trench to bury kitchen waste (the "bean trench") was standard advice in every Victorian gardening book and remains one of the best organic soil improvement methods.',
    imageUrl: '/images/plants/runner-bean.jpg',
    tags: ['summer', 'climber', 'legume', 'nitrogen-fixing', 'prolific', 'popular'],
  },
  {
    id: 'garlic',
    name: 'Garlic',
    latinName: 'Allium sativum',
    family: 'allium',
    emoji: '🧄',
    description: 'Garlic is the backbone of the kitchen garden and one of the easiest crops to grow. Plant in autumn, largely ignore through winter, and harvest magnificent bulbs the following summer.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [10, 11, 2, 3],
      harvest: [6, 7],
    },
    companions: ['rose', 'tomato', 'carrot', 'apple'],
    antagonists: ['bean', 'pea', 'asparagus'],
    soilType: 'Free-draining soil in an open, sunny position. Garlic dislikes cold, wet soils',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 30, plant: 15 },
    depthCm: 5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 150, max: 240 },
    cultivation: `## Varieties\nChoose between softneck varieties (better storage, no scapes) and hardneck varieties (more flavour, produce edible flower scapes). Use bulbs from a reputable supplier, not supermarket garlic which may carry disease.\n\n## Planting\nSeparate bulbs into individual cloves just before planting. Plant pointed end upward, tip just below the soil surface. In heavy soils, plant in modules of compost and transplant as young plants.\n\n## Winter Care\nGarlic needs a cold period (vernalisation) to form good bulbs — autumn planting is ideal. Weed regularly. In late spring, remove flower scapes (hardneck) to direct energy into bulb development (scapes are delicious in stir-fries).`,
    harvesting: `Harvest when lower leaves turn yellow and dry — usually June–July. Ease bulbs up with a fork; don't pull by the leaves. Lay out to dry in the sun for 2–3 weeks, or in a well-ventilated shed if weather is poor. Store in a cool, dry place.`,
    storage: 'Dried garlic keeps for 6–9 months. Braid softneck varieties into traditional ropes. Do not refrigerate — garlic sprouts in cold, damp conditions.',
    pests: ['Allium leaf miner', 'Leek rust', 'White rot (Sclerotium cepivorum)', 'Bulb mite'],
    diseases: ['White rot', 'Rust', 'Downy mildew', 'Fusarium rot'],
    organicTips: [
      'Plant in raised beds or on ridges to improve drainage and deter white rot',
      'Rotate alliums — never plant in the same ground two years running',
      'Avoid waterlogged conditions; if soil is heavy, grow in raised beds',
      'Plant near roses to deter aphids and black spot',
      'Use homemade garlic spray as a broad-spectrum pest deterrent',
    ],
    historicalNote: 'Garlic has been cultivated for over 5,000 years. Ancient Egyptian labourers building the pyramids were reportedly given garlic rations as a strength supplement. It fell out of favour with the Victorian English middle class as being too "continental" and pungent. Its re-embrace by British cooks came largely with the influence of Elizabeth David\'s writing in the 1950s.',
    nutritionFacts: 'Contains allicin (produced when crushed), a potent antimicrobial. Rich in manganese, vitamin B6, vitamin C, and selenium.',
    imageUrl: '/images/plants/garlic.jpg',
    tags: ['autumn-plant', 'winter-hardy', 'allium', 'easy', 'long-season', 'kitchen-staple'],
  },
  {
    id: 'lettuce',
    name: 'Lettuce',
    latinName: 'Lactuca sativa',
    family: 'leafy',
    emoji: '🥬',
    description: 'Lettuce can be harvested within weeks of sowing and provides leaves from early spring through autumn. Grow a cut-and-come-again mix for a constant supply without the gaps.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [2, 3, 4, 8, 9],
      sowOutdoors: [3, 4, 5, 6, 7, 8],
      transplant: [3, 4, 5, 6, 7, 8, 9],
      harvest: [4, 5, 6, 7, 8, 9, 10, 11],
    },
    companions: ['carrot', 'radish', 'strawberry', 'chive', 'dill'],
    antagonists: ['fennel', 'parsley'],
    soilType: 'Moist, fertile, moisture-retentive soil. Will tolerate partial shade in summer',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'partial',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 25 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 45, max: 80 },
    cultivation: `## Successional Sowing\nSow a short row every 2–3 weeks for a continuous supply. Lettuces hate heat — in midsummer, sow heat-resistant varieties in a shaded spot or use a cold frame.\n\n## Cut-and-Come-Again\nSow salad leaf mixes thickly and cut with scissors 2–3cm above the ground when 10cm tall. They regrow for 3–4 cuts. This is the most space-efficient way to grow salad.\n\n## Bolting\nOnce a lettuce "bolts" (runs to seed), the leaves turn bitter. Choose bolt-resistant varieties for summer, and harvest hearts promptly.`,
    harvesting: `Harvest hearted lettuces by cutting at the base with a sharp knife. Loose-leaf and cut-and-come-again types can be snipped little and often. Harvest in the morning when leaves are full of moisture.`,
    storage: 'Lettuces are best eaten straight from the garden. Store loosely in the fridge in a slightly damp cloth for 2–3 days. Never wash until ready to use.',
    pests: ['Slugs', 'Aphids', 'Cutworm', 'Root aphid'],
    diseases: ['Downy mildew', 'Grey mould (botrytis)', 'Lettuce drop'],
    organicTips: [
      'Grow in pots raised off the ground to deter slugs',
      'Lay coarse grit or crushed eggshells around plants as a slug barrier',
      'Use shade netting in midsummer to prevent bolting',
      'Water at the base of plants, not the leaves, to prevent grey mould',
    ],
    imageUrl: '/images/plants/lettuce.jpg',
    tags: ['leaf', 'cool-season', 'cut-and-come-again', 'container', 'beginner-friendly', 'quick-crop'],
  },
  {
    id: 'potato',
    name: 'Potato',
    latinName: 'Solanum tuberosum',
    family: 'solanaceae',
    emoji: '🥔',
    description: 'No kitchen garden is complete without potatoes. From waxy salad types to floury bakers, the variety available to the home grower vastly exceeds anything in the shops.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5],
      harvest: [6, 7, 8, 9, 10],
    },
    companions: ['bean', 'pea', 'sweetcorn', 'horseradish', 'marigold'],
    antagonists: ['tomato', 'cucumber', 'squash', 'sunflower', 'fennel'],
    soilType: 'Deep, well-cultivated, slightly acid soil with plenty of organic matter',
    soilPH: { min: 5.0, max: 6.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 70, plant: 30 },
    depthCm: 10,
    daysToGerminate: { min: 14, max: 28 },
    daysToHarvest: { min: 70, max: 120 },
    cultivation: `## Chitting\nStand seed potatoes rose-end up in egg boxes in a light, frost-free room from late January/February. Allow short, stubby chits to develop before planting. This gives the crop a head start.\n\n## Earthing Up\nAs shoots emerge, draw soil up around them to protect from frost and encourage tuber development. Repeat 2–3 times until a ridge 20–25cm high is formed. This also inhibits light reaching tubers (which turns them green and toxic).\n\n## Blight Management\nPotato blight (Phytophthora infestans) can devastate a crop in warm, wet weather. Grow blight-resistant varieties and apply Bordeaux mixture (copper sulphate) as a preventive spray.`,
    harvesting: `Early varieties: harvest when flowers open (or have fallen), about 10 weeks from planting. Ease the fork in away from the plant to avoid spearing tubers. Maincrops: leave until haulms (tops) have died back, then harvest on a dry day. Leave on the surface for an hour to dry before storing.`,
    storage: 'Store only sound, undamaged tubers in paper sacks or hessian bags in a cool, dark, frost-free shed. Light turns them green (solanine — mildly toxic). Remove any green tubers immediately.',
    pests: ['Wireworm', 'Slugs', 'Colorado beetle (notifiable — report to APHA)', 'Eelworm (nematodes)'],
    diseases: ['Blight (Phytophthora infestans)', 'Scab (common and powdery)', 'Blackleg', 'Mosaic virus'],
    organicTips: [
      'Choose certified seed potatoes to avoid imported disease',
      'Grow blight-resistant varieties such as Sarpo Mira or Carolus',
      'Bordeaux mixture (copper sulphate + lime) is permitted in organic growing as a blight preventive',
      'Rotate crops on a 4-year cycle — never grow potatoes or tomatoes in the same ground within 4 years',
      'Dig up and destroy any blight-affected haulms promptly — do not compost',
    ],
    historicalNote: 'The Irish Potato Famine (1845–1852) demonstrated the catastrophic danger of monoculture — over-reliance on a single variety (Lumper) with zero genetic resistance to blight. Modern organic growers deliberately maintain diversity in what they grow as a safeguard against such vulnerability.',
    nutritionFacts: 'An excellent source of vitamin C, potassium, vitamin B6, and fibre (particularly in the skin). Wrongly maligned as fattening — potatoes are naturally low in fat; it\'s the butter and cream we add that adds the calories.',
    imageUrl: '/images/plants/potato.jpg',
    tags: ['root', 'cool-season', 'starchy', 'staple', 'easy', 'popular'],
  },
  {
    id: 'courgette-basil',
    name: 'Basil',
    latinName: 'Ocimum basilicum',
    family: 'herb',
    emoji: '🌿',
    description: 'Sweet basil is the quintessential summer herb — aromatic, tender, and inseparable from tomatoes in the garden and on the plate. Needs warmth to thrive but generously rewards the attentive grower.',
    difficulty: 'moderate',
    biodynamicDay: 'flower',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [3, 4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [6, 7, 8, 9],
    },
    companions: ['tomato', 'pepper', 'asparagus'],
    antagonists: ['sage', 'thyme'],
    soilType: 'Light, free-draining, fertile soil or compost in a warm, sheltered position',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 20 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 40, max: 60 },
    cultivation: `## Warmth is Key\nBasil is a true heat lover — it sulks below 15°C and blackens in the cold. Start indoors in warmth (20–25°C) and don't rush to plant out. In the UK, a frost-free greenhouse or south-facing windowsill is ideal.\n\n## Pinching Out\nRegularly pinch out growing tips and flower buds to encourage bushy growth and delay bolting. Once basil flowers, the leaves lose much of their flavour.`,
    harvesting: 'Harvest from the top down, always leaving 2–3 pairs of leaves to regrow. Harvest in the morning for peak aromatic oils.',
    storage: 'Never refrigerate — basil blackens with cold. Store in a glass of water at room temperature like a bunch of flowers, loosely covered. For longer storage, blend into pesto and freeze, or preserve in olive oil.',
    pests: ['Aphids', 'Whitefly', 'Slugs'],
    diseases: ['Fusarium wilt', 'Basil downy mildew (a serious modern threat)', 'Damping off'],
    organicTips: [
      'Grow near tomatoes — reportedly improves tomato flavour and deters whitefly',
      'Water at the base only — wet leaves encourage disease',
      'Grow multiple small pots rather than one large one for more resilient harvesting',
    ],
    imageUrl: '/images/plants/basil.jpg',
    tags: ['herb', 'summer', 'aromatic', 'tender', 'container', 'kitchen-staple'],
  },
  {
    id: 'pea',
    name: 'Garden Pea',
    latinName: 'Pisum sativum',
    family: 'legume',
    emoji: '🫛',
    description: 'There is no finer experience in the kitchen garden than eating fresh peas straight from the pod, still sweet with the warmth of the sun. They taste nothing like frozen peas, and the experience is irreplaceable.',
    difficulty: 'easy',
    biodynamicDay: 'fruit',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [2, 3, 4, 5, 6, 10, 11],
      harvest: [5, 6, 7, 8],
    },
    companions: ['carrot', 'radish', 'mint', 'spinach', 'bean'],
    antagonists: ['onion', 'garlic', 'leek', 'shallot'],
    soilType: 'Well-drained, moderately fertile soil. Avoid freshly manured ground',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 60, plant: 7 },
    depthCm: 4,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Supports\nProvide pea sticks (twiggy branches from hazel or birch) or netting for the plants to climb. Even dwarf varieties benefit from some support.\n\n## Mice\nMice love freshly sown peas. Sow in lengths of guttering in the greenhouse or protect with wire mesh, or sow in modules and transplant as young plants.\n\n## Successional Sowing\nSow a short row every 3–4 weeks for a continuous harvest. Make a late October sowing of overwintering varieties (Aqua, Douce Provence) for an early spring crop.`,
    harvesting: 'Harvest when pods are plump but before the peas inside become starchy. Pick regularly — pods left on the plant signal it to stop producing. Mangetout and sugar snap types are harvested before the peas form.',
    storage: 'Eat immediately for best flavour. Blanch and freeze any surplus within hours of picking — peas start converting sugar to starch the moment they are picked.',
    pests: ['Pea moth (caterpillar inside pods)', 'Aphids', 'Birds (pigeons, sparrows)', 'Mice'],
    diseases: ['Powdery mildew', 'Pea root rot', 'Fusarium wilt', 'Pea wilt'],
    organicTips: [
      'Cover with fleece or netting to exclude pea moth and birds',
      'Sow in modules indoors to avoid mice eating seeds',
      'Dig root systems into the ground at season end — they fix significant nitrogen',
      'Pea tops (tendrils and shoots) are edible and delicious — harvest the tips of climbing plants',
    ],
    imageUrl: '/images/plants/peas.jpg',
    tags: ['legume', 'cool-season', 'nitrogen-fixing', 'direct-sow', 'quick-crop', 'popular'],
  },
  {
    id: 'kale',
    name: 'Kale',
    latinName: 'Brassica oleracea var. sabellica',
    family: 'brassica',
    emoji: '🥦',
    description: 'Hardy, nutritious, and almost indestructible, kale is the backbone of the winter vegetable garden. It shrugs off hard frosts (which actually improve its sweetness) and provides fresh greens when little else is growing.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [4, 5, 6],
      transplant: [6, 7],
      harvest: [9, 10, 11, 12, 1, 2, 3, 4],
    },
    companions: ['beet', 'celery', 'herb', 'onion', 'nasturtium'],
    antagonists: ['strawberry', 'tomato', 'fennel'],
    soilType: 'Firm, fertile, moisture-retentive soil with high nitrogen. Firm planting is important',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 60, plant: 45 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 12 },
    daysToHarvest: { min: 90, max: 120 },
    cultivation: `## Club Root Prevention\nAlways check soil pH — club root thrives in acid conditions. Lime the soil if necessary to raise pH to 7.0–7.5. Firm planting (using your heel to firm after transplanting) is traditional and important.\n\n## Net Against Cabbage White Butterfly\nCover brassicas with fine mesh from transplanting onwards to exclude cabbage white butterflies and pigeons. This is far more effective than any spray.`,
    harvesting: 'Harvest outer leaves from the bottom of the plant upwards. Leave the growing point intact to continue producing. Frost-sweetened kale in December–February is at its finest.',
    storage: 'Best fresh. Will keep 3–4 days in the fridge. Blanch and freeze surplus.',
    pests: ['Cabbage white butterfly caterpillar', 'Mealy cabbage aphid', 'Flea beetle', 'Pigeons'],
    diseases: ['Club root (Plasmodiophora brassicae)', 'Downy mildew', 'Ring spot'],
    organicTips: [
      'Fine mesh netting is the most effective organic protection — use from day one',
      'Raise soil pH to 7.0–7.5 to suppress club root',
      'Rotate brassicas on a strict 4-year cycle to prevent club root build-up',
      'Never compost club-root-infected material — burn or bin it',
    ],
    imageUrl: '/images/plants/kale.jpg',
    tags: ['brassica', 'winter-hardy', 'leaf', 'superfood', 'easy', 'long-season'],
  },

  // ── Alliums ────────────────────────────────────────────────────────────────

  {
    id: 'onion',
    name: 'Onion',
    latinName: 'Allium cepa',
    family: 'allium',
    emoji: '🧅',
    description: 'The backbone of the kitchen garden. Onions come in a wonderful range of shapes, sizes, and colours — round or oval, white, brown, or red — and there is a variety suited to almost every sowing season. Keep them clear of weeds and they will look after themselves.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [1, 2],
      sowOutdoors: [3, 4, 5],
      transplant: [3, 4],
      harvest: [7, 8, 9],
    },
    companions: ['carrot', 'chamomile', 'parsnip', 'beetroot'],
    antagonists: ['bean', 'pea'],
    soilType: 'Well-drained, fertile soil. Avoid freshly manured ground.',
    soilPH: { min: 6.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 25, plant: 10 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 100, max: 150 },
    cultivation: `## Sowing from Seed\nSow seeds under glass in midwinter, then move to a cold frame to harden off before planting out. In warmer weather, sow seeds thinly direct, 1cm deep in rows 25cm apart.\n\n## Planting Sets\nSets (small bulbs) are the easier option for beginners — plant in rows 25cm apart with 10cm between each set, just covering the tip. Firm in well to prevent birds pulling them up.\n\n## Thinning & Weeding\nThin seedlings to 10cm apart as they grow. Keep meticulously weed-free, as onions are poor competitors — weeds rob them of light and moisture.`,
    harvesting: `Harvest onions as needed through summer. For storing, wait until the foliage flops over naturally, then lift with a fork on a dry day. Lay on a wire rack or bench in the sun for 2–3 weeks to cure thoroughly — the skins should rustle like paper before storing.`,
    storage: 'Once cured, store in a cool, dark, well-ventilated place in nets or plaited ropes. Well-dried onions will store for months. Discard any with soft spots before storing.',
    pests: ['Onion fly', 'Onion eelworm', 'Thrips', 'Slugs'],
    diseases: ['White rot', 'Downy mildew', 'Neck rot', 'Botrytis'],
    organicTips: [
      'Companion plant with carrots — onion scent deters carrot fly; carrot scent deters onion fly',
      'Rotate alliums on a minimum 3-year cycle to prevent white rot build-up',
      'Never compost white-rot-affected bulbs — the spores persist for 20 years in soil',
      'Choose sets over seeds in cold, wet regions for more reliable results',
    ],
    historicalNote: 'Onions have been cultivated for over 5,000 years and were among the most important foods of ancient Egypt, where they were offered to the gods and found in the eye sockets of mummies. Crop rotation to avoid the devastating white rot fungus has been practised since at least the 18th century.',
    nutritionFacts: 'Rich in quercetin (a flavonoid antioxidant), vitamin C, and prebiotic fibre that feeds beneficial gut bacteria.',
    imageUrl: '/images/plants/onion.jpg',
    tags: ['allium', 'cool-season', 'store', 'popular', 'kitchen-staple'],
  },

  {
    id: 'spring-onion',
    name: 'Spring Onion',
    latinName: 'Allium cepa',
    family: 'allium',
    emoji: '🌱',
    description: 'Mild, fresh, and quick to grow, spring onions are one of the most rewarding vegetables for impatient gardeners. Sow successionally every few weeks and you will have bunches to pull from spring through to autumn.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 6, 7, 8],
      harvest: [5, 6, 7, 8, 9, 10],
    },
    companions: ['carrot', 'lettuce', 'beetroot'],
    antagonists: ['bean', 'pea'],
    soilType: 'Any fertile, well-drained soil.',
    soilPH: { min: 6.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 10, plant: 1 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 60, max: 80 },
    cultivation: `## Successional Sowing\nSow every three weeks from late winter to late summer to ensure a continuous supply. Sow thinly in rows 10cm apart, 1cm deep. No need to thin — simply pull as needed when pencil-thick.\n\n## Overwintering Varieties\nSome varieties (such as 'White Lisbon Winter Hardy') can be sown in August or September under cloches for early spring harvesting — invaluable for the hungry gap.`,
    harvesting: `Pull when the stems are pencil-thick, using a small fork to loosen the soil first. Thinning and harvesting are the same operation — use the thinnings in the kitchen. Eat fresh; spring onions do not store well.`,
    storage: 'Use within 2–3 days of harvest. Keep in a glass of cold water to refresh wilted bunches.',
    pests: ['Onion fly', 'Slugs', 'Aphids'],
    diseases: ['Downy mildew', 'White rot'],
    organicTips: [
      'Sow under fleece from March for earlier harvests',
      'Sow in late summer under cloches for spring crops through the hungry gap',
      'Successional sowings every 3 weeks prevent feast-and-famine cycles',
    ],
    imageUrl: '/images/plants/spring-onion.jpg',
    tags: ['allium', 'quick-growing', 'salad', 'container', 'succession'],
  },

  {
    id: 'leek',
    name: 'Leek',
    latinName: 'Allium porrum',
    family: 'allium',
    emoji: '🌿',
    description: 'Leeks are the patient gardener\'s reward — slow to grow, but steadfastly hardy, they stand in the ground through the darkest months when almost nothing else does. A slow-cooked leek gratin on a winter evening justifies every week they spent growing.',
    difficulty: 'moderate',
    biodynamicDay: 'root',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [2, 3, 4],
      transplant: [6, 7],
      harvest: [9, 10, 11, 12, 1, 2],
    },
    companions: ['carrot', 'celery', 'onion'],
    antagonists: ['bean', 'pea'],
    soilType: 'Deep, rich, well-cultivated soil. Prepare with plenty of well-rotted manure or compost.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 15 },
    depthCm: 15,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 120, max: 180 },
    cultivation: `## Sowing\nSow thinly indoors from February to April, 1cm deep in trays of seed compost. Grow on in a cold frame.\n\n## Transplanting\nWhen seedlings are pencil-thick (about June–July), transplant to their final positions. Use a dibber to make holes 15cm deep; drop a single seedling in and water in — do not firm the soil. The hole gradually fills as the plant grows, blanching the stem naturally.\n\n## Earthing Up\nAs the plants develop, draw soil up around the stems to extend the blanched (white) portion and improve flavour. This is the traditional method of improving quality.`,
    harvesting: `Lift leeks with a fork to avoid breaking the stems — they are brittle. Harvest as needed from autumn, when they are finger-thick. Most varieties will stand happily in the ground until March. Once lifted, trim the roots and darkest outer leaves.`,
    storage: 'Best left in the ground until needed. Once lifted, store in the salad drawer of the fridge for up to a week.',
    pests: ['Leek moth', 'Onion fly', 'Thrips', 'Allium leaf miner'],
    diseases: ['Leek rust', 'White rot', 'Downy mildew'],
    organicTips: [
      'Cover with fine mesh in June to exclude leek moth and allium leaf miner',
      'Earthing up improves the blanched shank without any additional effort',
      'Rotate leeks on a 3-year cycle with other alliums',
      'Harvest from October — they improve in flavour after the first hard frosts',
    ],
    historicalNote: 'The leek is the national emblem of Wales, reputedly worn by Welsh soldiers in a battle against the Saxons to distinguish friend from foe. It has been cultivated in Britain since at least the Dark Ages and was a mainstay of the cottage garden through every winter.',
    imageUrl: '/images/plants/leek.jpg',
    tags: ['allium', 'winter-hardy', 'long-season', 'root', 'store'],
  },

  {
    id: 'shallot',
    name: 'Shallot',
    latinName: 'Allium cepa (Aggregatum group)',
    family: 'allium',
    emoji: '🧅',
    description: 'Sweeter and more nuanced in flavour than the common onion, shallots grow in handsome clusters and store beautifully. They come in a wider variety of shapes and colours than most gardeners realise — from banana shallots to small round red types.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'transplant',
    calendar: {
      sowOutdoors: [2, 3, 4],
      harvest: [7, 8, 9],
    },
    companions: ['carrot', 'chamomile', 'strawberry'],
    antagonists: ['bean', 'pea'],
    soilType: 'Well-drained, fertile soil in a sunny position. Avoid freshly manured ground.',
    soilPH: { min: 6.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 25, plant: 18 },
    depthCm: 3,
    daysToGerminate: { min: 10, max: 14 },
    daysToHarvest: { min: 90, max: 120 },
    cultivation: `## Planting Sets\nFor the best results, plant sets (small bulbs) in late winter to early spring, as soon as the ground can be worked. Plant with the pointed tip just showing above the surface, in rows 25cm apart with 18cm between bulbs.\n\n## Frost Lifting\nIn very cold or frosty spells, sets can be pushed out of the ground by frost action. Check rows after hard weather and firm any displaced sets back in gently.\n\n## Seeds vs Sets\nSeeds can be sown in early winter in mild areas, but sets are far more reliable and are the method recommended for most gardens.`,
    harvesting: `Lift the clumps when the foliage has turned yellow and begun to wither. Carefully separate the bulbs and spread on a wire rack in the sun to cure for 2–3 weeks. The leaves and outer skins should feel papery before storing.`,
    storage: 'Store in nets or spread on trays in a cool, dry, airy place. Properly cured shallots will keep until spring. Check periodically and remove any that are softening.',
    pests: ['Onion fly', 'Thrips', 'Eelworm'],
    diseases: ['White rot', 'Downy mildew', 'Neck rot'],
    organicTips: [
      'Plant sets rather than seeds for reliable results in short-season gardens',
      'Choose certified disease-free sets to avoid introducing white rot to clean soil',
      'Rotate alliums on a minimum 3-year cycle',
    ],
    imageUrl: '/images/plants/shallots.jpg',
    tags: ['allium', 'cool-season', 'store', 'flavour', 'kitchen-staple'],
  },

  // ── Brassicas ──────────────────────────────────────────────────────────────

  {
    id: 'cauliflower',
    name: 'Cauliflower',
    latinName: 'Brassica oleracea var. botrytis',
    family: 'brassica',
    emoji: '🥦',
    description: 'The most demanding of the brassica family, cauliflowers reward careful preparation and attention with their magnificent curds. Grow them and you will understand why they once commanded a premium at market. They come in summer, autumn, and winter varieties to extend the season across the year.',
    difficulty: 'challenging',
    biodynamicDay: 'flower',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4, 5, 6],
      transplant: [5, 6, 7],
      harvest: [8, 9, 10, 11, 12, 1],
    },
    companions: ['celery', 'dill', 'chamomile'],
    antagonists: ['strawberry', 'tomato'],
    soilType: 'Firm, deeply-dug, fertile loam. Cauliflowers need the most fertile soil of any brassica.',
    soilPH: { min: 6.8, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 60, plant: 60 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 80, max: 120 },
    cultivation: `## Varieties\nSow summer and autumn cauliflowers from March to May; winter varieties in May and June. Choosing the right variety for the intended harvest season is essential.\n\n## Soil Preparation\nCauliflowers are hungry plants — prepare the bed the previous autumn with generous amounts of well-rotted manure or compost. The soil must be firm, not loose.\n\n## Transplanting\nPlant out at 60cm spacing in a grid. Water in well and firm with your heel. Covering the roots with fine mesh at transplanting is advisable to exclude cabbage white butterflies from the outset.\n\n## Protecting the Curd\nOnce the white curd begins to form, fold inner leaves over it to protect from frost and sunlight. This prevents discolouration and keeps the curd tight and white.`,
    harvesting: `Cut when the curd is firm and tight, before individual florets begin to separate. Harvest with a sharp knife, leaving some protective leaves attached. Cauliflowers do not wait — check them daily once the curd appears. Hang upside down in a cool shed to store for a week or two.`,
    storage: 'Keep for up to 2 weeks stored upside down in a cool, dark shed. For longer storage, blanch florets and freeze.',
    pests: ['Cabbage white butterfly caterpillar', 'Cabbage root fly', 'Mealy aphid', 'Flea beetle'],
    diseases: ['Club root', 'Downy mildew', 'White blister', 'Ring spot'],
    organicTips: [
      'Net from transplanting to exclude cabbage white butterflies — non-negotiable',
      'Raise soil pH to at least 7.0 to suppress club root',
      'Firm transplants well — loose roots cause "blown" or loosely-formed curds',
      'Prepare soil with a green manure the previous season for the best results',
    ],
    imageUrl: '/images/plants/cauliflower.jpg',
    tags: ['brassica', 'autumn', 'winter', 'challenging', 'kitchen-staple'],
  },

  {
    id: 'cabbage',
    name: 'Cabbage',
    latinName: 'Brassica oleracea var. capitata',
    family: 'brassica',
    emoji: '🥬',
    description: 'Cabbages are the most versatile brassica, with varieties for every season of the year. From the delicate spring greens of April to the enormous January King that stands blue-frosted in the winter garden, no allotment is complete without them.',
    difficulty: 'moderate',
    biodynamicDay: 'leaf',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [1, 2, 3, 4, 5, 6, 7],
      transplant: [4, 5, 6, 7, 8],
      harvest: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    },
    companions: ['dill', 'chamomile', 'mint', 'rosemary'],
    antagonists: ['strawberry', 'tomato', 'grape'],
    soilType: 'Rich, firm, well-cultivated soil. Add lime if pH is below 6.8.',
    soilPH: { min: 6.8, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 50, plant: 45 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 12 },
    daysToHarvest: { min: 70, max: 180 },
    cultivation: `## Varieties by Season\nSow summer and autumn cabbages from January to June; they harvest from July to November. Sow winter cabbages in June and July for harvesting October through to April. Chinese cabbages are sown in summer for autumn use.\n\n## Transplanting\nWhen seedlings have 4–5 leaves, transplant to their final spacing, firming well with your heel. Loose planting results in leafy, open heads that do not heart up properly.\n\n## Aftercare\nKeep well weeded and water in dry spells. Earth up around the stem base if plants rock in wind — a loose root inhibits head formation.\n\n## Harvesting\nCut the younger leaves from the centre — the heart. Remove the root after harvesting to avoid encouraging disease and pests in the soil.`,
    harvesting: `Cut the heart when firm and solid. Leave the stalk in the ground after harvesting a summer cabbage — it will often sprout a secondary crop of small greens in autumn. For spring cabbage, harvest regularly as greens through winter before allowing to heart up.`,
    storage: 'Winter varieties can be left in the ground until needed. Once cut, store in a cool shed for several weeks. Slice and freeze any surplus.',
    pests: ['Cabbage white butterfly caterpillar', 'Cabbage root fly', 'Slugs', 'Pigeons', 'Mealy aphid'],
    diseases: ['Club root', 'Downy mildew', 'Ring spot'],
    organicTips: [
      'Fine mesh netting is the single most effective organic protection for all brassicas',
      'Rotate on a strict 4-year cycle to prevent club root',
      'Firm transplanting is critical — use your heel to press soil around each plant',
      'Scatter brassica collars around the stem base to exclude cabbage root fly',
    ],
    historicalNote: 'Cabbage has been grown in Britain since before Roman times and was considered medicinal as well as culinary. The tradition of planting cabbages on Good Friday — a day considered auspicious for root work — survived in some rural communities into the 20th century.',
    imageUrl: '/images/plants/cabbage.jpg',
    tags: ['brassica', 'cool-season', 'year-round', 'kitchen-staple', 'hardy'],
  },

  {
    id: 'brussels-sprouts',
    name: 'Brussels Sprouts',
    latinName: 'Brassica oleracea var. gemmifera',
    family: 'brassica',
    emoji: '🥦',
    description: 'Much maligned but deeply misunderstood, Brussels sprouts are one of the garden\'s great autumn and winter vegetables. Grown properly — and eaten fresh rather than boiled to oblivion — they are sweet, nutty, and delicious. They are also one of the most frost-resistant vegetables you can grow.',
    difficulty: 'moderate',
    biodynamicDay: 'leaf',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4, 5],
      transplant: [5, 6, 7],
      harvest: [10, 11, 12, 1, 2, 3],
    },
    companions: ['dill', 'chamomile', 'mint'],
    antagonists: ['strawberry', 'tomato'],
    soilType: 'Firm, rich, well-limed soil. Brussels sprouts need more stability than any other brassica — loose soil produces blown sprouts.',
    soilPH: { min: 6.8, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 75, plant: 75 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 12 },
    daysToHarvest: { min: 120, max: 180 },
    cultivation: `## Sowing\nSow in early to mid-spring, 1cm deep in rows as thinly as possible. Growing from seed produces better results than buying transplants, as plants can be hardened off gradually.\n\n## Transplanting\nWhen seedlings reach 10–12cm, transplant to their final positions at 75cm spacings — they need the room. Firm in very thoroughly, using your heel. Brussels sprouts more than any other vegetable will produce poor, blown sprouts if the root is loose.\n\n## Staking\nStake plants in exposed gardens — tall plants in autumn gales can rock and the root becomes loose. Earth up around the base too.\n\n## Harvesting Technique\nPick the sprouts from the bottom of the stem first, working upwards. Take only a few from each plant at a time — this keeps the plant productive from mid-autumn through to spring.`,
    harvesting: `Pick from the bottom upwards, taking only firm, tight sprouts. Avoid picking loose, open ones — these have been produced by loose planting or poor soil. Continue harvesting through to spring, allowing the plant to produce new sprouts above the picked section.`,
    storage: 'Best left on the plant and picked fresh. Once picked, refrigerate and use within a few days. Blanch and freeze any surplus.',
    pests: ['Cabbage white butterfly', 'Mealy aphid', 'Cabbage root fly', 'Pigeons'],
    diseases: ['Club root', 'Downy mildew', 'Ring spot'],
    organicTips: [
      'The single most important factor in good sprouts is firm soil and firm planting',
      'Net from transplanting to exclude butterflies and pigeons',
      'Frost improves the flavour — the starches convert to sugars at low temperature',
      'Raise soil pH above 7.0 to suppress club root',
    ],
    historicalNote: 'Brussels sprouts were developed in Belgium (hence the name) around the 13th century and became a common crop in northern European kitchen gardens by the 18th century. Their association with Christmas dinner is a relatively modern British tradition, dating mainly from the Victorian era.',
    imageUrl: '/images/plants/brussels-sprout.jpg',
    tags: ['brassica', 'winter-hardy', 'long-season', 'christmas', 'challenging'],
  },

  {
    id: 'spinach',
    name: 'Spinach',
    latinName: 'Spinacia oleracea',
    family: 'leafy',
    emoji: '🌿',
    description: 'Fast-growing and packed with iron and nutrients, spinach is one of the most rewarding crops to grow. Choose a bolt-resistant variety and sow successionally to maintain a steady supply through the cooler months.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 8, 9],
      harvest: [5, 6, 7, 8, 10, 11],
    },
    companions: ['strawberry', 'pea', 'bean', 'celery'],
    antagonists: [],
    soilType: 'Rich, moisture-retentive loam with plenty of added compost. Avoid sandy, dry soils.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 30, plant: 10 },
    depthCm: 2,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 40, max: 70 },
    cultivation: `## Soil Preparation\nFor the best spinach, incorporate generous amounts of garden compost into the bed the previous autumn. Spinach needs a rich, moisture-retentive soil to grow quickly and avoid bolting.\n\n## Sowing\nSow thinly, direct in the ground. Spring sowings will produce through early summer; late summer and autumn sowings crop through winter and early spring when the hungry gap is approaching.\n\n## Preventing Bolting\nSpinach runs to seed quickly in heat. Choose bolt-resistant varieties for spring sowing, and keep well watered in dry spells. Summer sowings in partial shade can extend the season.`,
    harvesting: `Remove the outer leaves, leaving the growing heart intact to produce more. Pick frequently — this is more productive than leaving plants to grow large. Young leaves are the most tender and flavoursome.`,
    storage: 'Best eaten immediately after picking. Will wilt in hours. Blanch and freeze any surplus.',
    pests: ['Slugs', 'Aphids', 'Leaf miner'],
    diseases: ['Downy mildew', 'Leaf spot'],
    organicTips: [
      'Sow in autumn for spring crops through the hungry gap',
      'Water regularly — drought is the primary cause of bolting',
      'Grow in partial shade in summer to delay bolting',
      'Choose bolt-resistant varieties such as \'Tornado\' or \'Bloomsdale\'',
    ],
    nutritionFacts: 'One of the most nutrient-dense vegetables: exceptionally rich in iron, calcium, magnesium, vitamins K, A, and C, and folate. Lightly cooking spinach increases iron bioavailability.',
    imageUrl: '/images/plants/spinach.jpg',
    tags: ['leafy', 'quick-growing', 'cool-season', 'superfood', 'succession'],
  },

  // ── Root Vegetables ────────────────────────────────────────────────────────

  {
    id: 'beetroot',
    name: 'Beetroot',
    latinName: 'Beta vulgaris',
    family: 'root',
    emoji: '🫐',
    description: 'Deep, jewel-red, and earthy-sweet, beetroot is one of the most satisfying roots to pull from the ground. Eaten raw in salads, roasted until caramelised, or pickled in vinegar — its versatility in the kitchen matches its ease in the garden.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 6, 7],
      harvest: [6, 7, 8, 9, 10, 11],
    },
    companions: ['onion', 'lettuce', 'kohlrabi', 'brassica'],
    antagonists: ['bean', 'charlock'],
    soilType: 'Well-drained, stone-free soil. Avoid freshly manured ground.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 8 },
    depthCm: 3,
    daysToGerminate: { min: 10, max: 14 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Sowing\nEach beetroot "seed" is actually a cluster of 2–3 seeds, so even careful sowing will require thinning. Sow 3cm deep in rows 30cm apart. Sow in succession every 3–4 weeks from March to July for continuous cropping.\n\n## Thinning\nThin to 8cm apart when seedlings are large enough to handle. The thinnings are edible as micro-salad greens.\n\n## Watering\nConsistent watering is important — erratic wet and dry spells cause the roots to split. Mulching helps retain moisture.`,
    harvesting: `Pull smaller beets from the row as required — this thins the row and lets the remaining roots develop. For storage, harvest before the first hard frosts, when roots are golf-ball to tennis-ball size. Twist off the leaves 3cm above the root to prevent bleeding.`,
    storage: 'Store in boxes of barely moist sand in a cool, frost-free shed. Will keep for several months. Alternatively, pickle in vinegar for long storage.',
    pests: ['Aphids', 'Leaf miners', 'Slugs'],
    diseases: ['Downy mildew', 'Leaf spot', 'Boron deficiency (hollow root)'],
    organicTips: [
      'Sow successionally every 3 weeks for continuous picking',
      'Twist rather than cut tops off to prevent the roots from "bleeding"',
      'Young leaves are excellent in salads — pick a few without removing too much from young plants',
      'A light dressing of wood ash at sowing time provides boron, which prevents hollow root',
    ],
    nutritionFacts: 'Rich in folate, manganese, and dietary nitrates — the latter have been shown to improve athletic endurance. The deep red pigment (betalain) is a potent antioxidant.',
    imageUrl: '/images/plants/beetroot.jpg',
    tags: ['root', 'cool-season', 'direct-sow', 'store', 'pickle', 'easy'],
  },

  {
    id: 'parsnip',
    name: 'Parsnip',
    latinName: 'Pastinaca sativa',
    family: 'root',
    emoji: '🥕',
    description: 'The true taste of winter. Roasted parsnips caramelise and sweeten in the oven in a way that makes them almost addictive. They are one of the slowest vegetables to germinate — patience is required — but once established they look after themselves completely.',
    difficulty: 'moderate',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [2, 3, 4, 5],
      harvest: [10, 11, 12, 1, 2, 3],
    },
    companions: ['pea', 'bean', 'radish'],
    antagonists: ['carrot'],
    soilType: 'Deep, light, stone-free soil. Avoid freshly manured ground (causes forking).',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 30, plant: 15 },
    depthCm: 1,
    daysToGerminate: { min: 14, max: 28 },
    daysToHarvest: { min: 120, max: 180 },
    cultivation: `## Patience with Germination\nParsnip seed is notoriously slow and erratic to germinate — do not give up. Use fresh seed each year (old seed germinates very poorly). Sow radishes in the same row as markers; the radishes will be harvested by the time the parsnips need the space.\n\n## Sowing\nSow direct, 1cm deep in rows 30cm apart, from February to May. In cold soils, cover with fleece to warm the ground and improve germination rates.\n\n## Thinning\nThin to 15cm when large enough to handle. Water well during dry spells in the growing season.`,
    harvesting: `Parsnips can be left in the ground and harvested as needed throughout winter. The flavour is at its best after the first frosts — cold temperatures convert starches to sugars, producing the characteristic sweetness. Lift carefully with a fork to avoid breaking the long roots.`,
    storage: 'Best left in the ground until needed — this is their natural storage. Once lifted, store in boxes of barely moist sand in a cool shed. They deteriorate quickly if lifted and left in warm conditions.',
    pests: ['Carrot fly', 'Celery fly', 'Slugs', 'Wireworm'],
    diseases: ['Parsnip canker', 'Sclerotinia rot', 'Powdery mildew'],
    organicTips: [
      'Use fresh seed every year — germination rates fall sharply after one season',
      'Sow radishes as row markers and intercrop companions to make use of the long waiting time',
      'Leave in the ground until after the first frosts for the sweetest flavour',
      'Fine mesh over the bed deters carrot fly, which also attacks parsnips',
    ],
    historicalNote: 'Before the arrival of the potato, parsnips were one of the primary starchy foods of the British Isles and were used to make wine, beer, and even jam. They were considered a luxury enough to appear on medieval feast tables.',
    imageUrl: '/images/plants/parsnip.jpg',
    tags: ['root', 'winter', 'frost-sweetened', 'long-season', 'store'],
  },

  {
    id: 'radish',
    name: 'Radish',
    latinName: 'Raphanus sativus',
    family: 'root',
    emoji: '🔴',
    description: 'The fastest-growing vegetable in the garden — a radish sown today can be on the salad plate in under a month. Crisp, peppery, and available in an extraordinary range of colours and shapes, they are perfect for filling gaps between slower-growing crops.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 6, 7, 8, 9],
      harvest: [4, 5, 6, 7, 8, 9, 10],
    },
    companions: ['lettuce', 'cucumber', 'pea', 'carrot', 'parsnip'],
    antagonists: ['hyssop'],
    soilType: 'Any fertile, well-drained soil. Summer varieties need moisture-retentive ground.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 15, plant: 3 },
    depthCm: 1,
    daysToGerminate: { min: 4, max: 7 },
    daysToHarvest: { min: 25, max: 60 },
    cultivation: `## Successional Sowing\nSow little and often — every 2–3 weeks from March to September — as radishes run to seed very quickly in warm weather. A single large sowing is difficult to use before they become fibrous and hollow.\n\n## Intercropping\nSow radishes as row markers for slower-germinating crops like parsnip and carrot, or in the gaps between brassicas and other slow-maturing plants.\n\n## Summer Varieties\nWinter radishes (such as daikon/mooli and Black Spanish) are sown in summer and autumn. They are much larger, milder, and store well.`,
    harvesting: `Pull young — radishes are at their best when marble-sized. Check every 2–3 days once foliage appears above ground. Eat immediately for the best crunch; they soften within hours of harvesting. Winter varieties can remain in the ground, protected by fleece.`,
    storage: 'Eat summer radishes fresh on the day of harvest. Winter types can be stored in boxes of damp sand for several months.',
    pests: ['Flea beetle', 'Cabbage root fly', 'Slugs'],
    diseases: ['Club root', 'Downy mildew'],
    organicTips: [
      'Intercrop with slow-growing plants to make use of the space and suppress weeds',
      'Sow under fleece in early March for first crops in April',
      'Avoid sowing in summer heat — radishes bolt immediately; choose heat-tolerant varieties',
      'Water regularly to prevent hollow, fibrous roots',
    ],
    imageUrl: '/images/plants/radish.jpg',
    tags: ['root', 'quick-growing', 'cool-season', 'beginner', 'succession', 'salad'],
  },

  {
    id: 'broad-bean',
    name: 'Broad Bean',
    latinName: 'Vicia faba',
    family: 'legume',
    emoji: '🫘',
    description: 'The hardiest of all beans and one of the oldest cultivated plants in Britain, broad beans are among the first crops of the year — sown in autumn or late winter, they fill the hungry gap in May and June when the garden is still coming to life. Eaten young and sweet, straight from the pod, nothing compares.',
    difficulty: 'easy',
    biodynamicDay: 'flower',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [2, 3, 4, 10, 11],
      harvest: [5, 6, 7, 8],
    },
    companions: ['carrot', 'summer-savory', 'potato', 'brassica'],
    antagonists: ['onion', 'fennel', 'garlic'],
    soilType: 'Well-drained, fertile soil. Will grow in most soils including heavier clay.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 45, plant: 23 },
    depthCm: 5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 90, max: 150 },
    cultivation: `## Autumn Sowing\nOverwintering varieties (such as 'Aquadulce Claudia') can be sown in October–November. They will germinate, develop slowly through winter, and produce an early crop in May — 4–6 weeks ahead of spring-sown plants. Protect with cloches in harsh winters.\n\n## Spring Sowing\nSow 4–5cm deep and 23cm apart in double rows. Allow 45cm between double rows for access.\n\n## Pinching Out\nWhen the first pods start to form, pinch out the growing tips of the plant. This directs energy into pod development and removes the tender tips that blackfly find most attractive.`,
    harvesting: `Pick the pods when the beans inside are just visible as swellings, before the inner skin becomes tough and grey. Immature pods can be eaten whole like French beans. The shoot tips, pinched out in late spring, can be briefly wilted in butter and eaten as a vegetable in their own right.`,
    storage: 'Best eaten immediately — flavour deteriorates quickly. Blanch and freeze within hours of picking for long-term storage. Fully mature seeds can be dried as dried beans.',
    pests: ['Black bean aphid (blackfly)', 'Pea and bean weevil', 'Slugs', 'Mice'],
    diseases: ['Chocolate spot (Botrytis fabae)', 'Rust', 'Root rot'],
    organicTips: [
      'Autumn sowing produces the earliest crop and largely avoids blackfly',
      'Pinch out growing tips when the lowest pods are forming — removes the blackfly colony habitat',
      'Sow spare seeds to fill any gaps, as germination in cold soil can be uneven',
      'Leave roots in the ground at season end — they are packed with fixed nitrogen',
    ],
    historicalNote: 'Broad beans have been cultivated in Britain since the Bronze Age. Before the arrival of New World beans (runner beans, French beans) in the 16th century, the broad bean was the only bean grown in European gardens. Feared "bean fever" — a genetic condition affecting certain Mediterranean populations — does not apply to British-grown broad beans.',
    nutritionFacts: 'Excellent source of plant protein, folate, manganese, and copper. One of the few vegetables rich in both protein and fibre simultaneously.',
    imageUrl: '/images/plants/broad-bean.jpg',
    tags: ['legume', 'cool-season', 'autumn-sow', 'nitrogen-fixing', 'hungry-gap', 'easy'],
  },

  {
    id: 'french-bean',
    name: 'French Bean',
    latinName: 'Phaseolus vulgaris',
    family: 'legume',
    emoji: '🫛',
    description: 'Smaller, rounder, and more delicate than runner beans, French beans come in climbing and dwarf varieties, in a remarkable range of colours from yellow to purple. They are tender, quick-cropping, and ideal for containers and small gardens.',
    difficulty: 'easy',
    biodynamicDay: 'flower',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['carrot', 'courgette', 'strawberry', 'brassica'],
    antagonists: ['onion', 'fennel', 'garlic'],
    soilType: 'Well-drained, fertile soil. Will not tolerate waterlogging.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 45, plant: 10 },
    depthCm: 4,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 50, max: 70 },
    cultivation: `## Protection from Frost\nFrench beans are frost-tender — sow under glass if there is any risk of cold nights. Direct sow from late May when the soil has warmed to at least 12°C.\n\n## Climbing vs Dwarf\nClimbing varieties are more productive and need support — build canes or a trellis before sowing. Dwarf varieties need no support and are excellent for containers.\n\n## Watering\nWater consistently, particularly once plants are in flower. Irregular watering causes pods to drop or misshape.`,
    harvesting: `Pick when the pods are still slender and the beans inside have not yet swelled visibly. At this stage they are tender and stringless. Pick every 2–3 days — pods left on the plant divert energy away from further flowering. Surplus pods can be dried on the plant as haricot beans.`,
    storage: 'Best eaten fresh. Blanch and freeze any surplus. Fully dried beans can be stored in jars for a year or more.',
    pests: ['Black bean aphid', 'Slugs', 'Bean seed fly', 'Mexican bean beetle'],
    diseases: ['Halo blight', 'Anthracnose', 'Botrytis'],
    organicTips: [
      'Sow indoors in small pots to give a head start over direct-sown competitors',
      'Purple-podded varieties are easier to spot at harvest time',
      'Dig roots into the ground at season end — they fix nitrogen',
      'Pinch out growing tips of climbing varieties at the top of the support to encourage bushing',
    ],
    imageUrl: '/images/plants/french-bean.jpg',
    tags: ['legume', 'warm-season', 'container', 'quick-growing', 'nitrogen-fixing'],
  },

  // ── Cucurbits ──────────────────────────────────────────────────────────────

  {
    id: 'pumpkin',
    name: 'Pumpkin',
    latinName: 'Cucurbita maxima',
    family: 'cucurbit',
    emoji: '🎃',
    description: 'Pumpkins reward gardeners with the most dramatic vegetables in the garden — some growing to enormous sizes. Beyond their Halloween role as lanterns, they make outstanding soups, pies, and curries. Their trailing habit means they can be trained over a fence or compost heap, making clever use of otherwise wasted space.',
    difficulty: 'easy',
    biodynamicDay: 'fruit',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [4, 5],
      transplant: [5, 6],
      harvest: [9, 10],
    },
    companions: ['sweetcorn', 'bean', 'nasturtium'],
    antagonists: ['potato', 'fennel'],
    soilType: 'Very rich, moisture-retentive soil. Ideally grown over a buried compost or manure pit.',
    soilPH: { min: 5.5, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 200, plant: 150 },
    depthCm: 3,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 90, max: 120 },
    cultivation: `## Sowing\nSoak seeds overnight before sowing on their edge (not flat) in individual pots of multipurpose compost. Keep at 18–20°C. Sow in late April or May and transplant after all risk of frost has passed.\n\n## Space Requirements\nPumpkins are vigorous spreading plants — allow 1.5–2m between plants. In small gardens, train the trailing stems upwards over a trellis or grow smaller bush varieties.\n\n## Feeding\nPumpkins are hungry plants. Once fruits have set, feed weekly with a high-potash organic feed (comfrey tea is ideal).`,
    harvesting: `Harvest before the first frosts. The skin should be hard enough that a fingernail cannot easily pierce it. Cut with a generous stalk attached — a short stalk causes the fruit to rot. Leave in the sun for 10–14 days to cure the skin before storing.`,
    storage: 'After curing, store in a cool, dry, frost-free shed on wooden slats (not concrete). Well-cured pumpkins will keep for 3–6 months.',
    pests: ['Slugs (on young plants)', 'Vine weevil', 'Aphids', 'Red spider mite'],
    diseases: ['Powdery mildew (late season)', 'Cucumber mosaic virus', 'Botrytis'],
    organicTips: [
      'Sow on a mound over a buried pit of compost or manure for the most vigorous growth',
      'Spray diluted milk on leaves to suppress powdery mildew',
      'Hand-pollinate in cool, wet summers — transfer pollen from male to female flowers',
      'Slide a tile or piece of wood under each developing fruit to prevent rotting on wet soil',
    ],
    imageUrl: '/images/plants/pumpkin.jpg',
    tags: ['cucurbit', 'warm-season', 'autumn', 'halloween', 'store'],
  },

  {
    id: 'cucumber',
    name: 'Cucumber',
    latinName: 'Cucumis sativus',
    family: 'cucurbit',
    emoji: '🥒',
    description: 'Crisp, cool, and refreshing, cucumbers are one of summer\'s great pleasures. Modern outdoor and ridge varieties mean greenhouse space is no longer essential, though greenhouse cucumbers will always be more productive in the British climate.',
    difficulty: 'moderate',
    biodynamicDay: 'fruit',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4, 5],
      transplant: [5, 6],
      harvest: [7, 8, 9],
    },
    companions: ['bean', 'pea', 'nasturtium', 'sunflower'],
    antagonists: ['potato', 'sage', 'fennel'],
    soilType: 'Rich, free-draining, moisture-retentive soil with generous organic matter.',
    soilPH: { min: 5.5, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 90, plant: 60 },
    depthCm: 2,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 50, max: 70 },
    cultivation: `## Sowing\nSow 2–3 seeds per pot in early spring, remove the weaker seedlings and grow on at 18–24°C. Cucumbers hate root disturbance, so transplant carefully.\n\n## Greenhouse Varieties\nGreenhouse cucumbers are all-female (parthenocarpic) varieties that do not require pollination. Pinch out the growing tip when it reaches the greenhouse roof.\n\n## Outdoor Varieties\nOutdoor (ridge) varieties are more tolerant of cool, wet conditions. Mulch the base heavily with straw to prevent the developing fruit from rotting on the soil.`,
    harvesting: `Harvest when they reach the desired size — do not wait for them to yellow, as this indicates they are past their best. Cut with a sharp knife rather than twisting. Regular harvesting promotes continued cropping.`,
    storage: 'Eat on the day of harvest. Will keep 2–3 days wrapped in the fridge. Do not freeze.',
    pests: ['Aphids', 'Red spider mite', 'Whitefly', 'Slugs'],
    diseases: ['Powdery mildew', 'Cucumber mosaic virus', 'Botrytis', 'Root rot'],
    organicTips: [
      'Mist plants regularly in a greenhouse to raise humidity and deter red spider mite',
      'Introduce biological controls (Phytoseiulus persimilis) to manage red spider mite',
      'Mulch outdoor plants with straw to retain moisture and protect fruit',
      'Remove any yellowing lower leaves promptly to reduce fungal disease risk',
    ],
    imageUrl: '/images/plants/cucumber.jpg',
    tags: ['cucurbit', 'warm-season', 'greenhouse', 'salad', 'summer'],
  },

  // ── Other Vegetables ────────────────────────────────────────────────────────

  {
    id: 'asparagus',
    name: 'Asparagus',
    latinName: 'Asparagus officinalis',
    family: 'root',
    emoji: '🌿',
    description: 'An investment in the future. Asparagus takes three years before you can harvest your first cut spears, but once established it will crop reliably for twenty years or more. That first May morning when you cut fresh spears, drizzle them with butter, and eat them standing in the garden makes every year of waiting worthwhile.',
    difficulty: 'challenging',
    biodynamicDay: 'flower',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4],
      transplant: [4, 5],
      harvest: [4, 5, 6],
    },
    companions: ['tomato', 'parsley', 'basil'],
    antagonists: ['onion', 'garlic', 'potato'],
    soilType: 'Deep, free-draining, sandy loam. Asparagus will not tolerate waterlogged soil.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 45, plant: 45 },
    depthCm: 20,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 730, max: 1095 },
    cultivation: `## Planting Crowns\nBuying one-year-old crowns gives a year's head start over growing from seed. Plant in spring in a trench 20cm deep with a ridge of soil running along the base. Spread the roots of each crown over the ridge and cover with 5cm of soil, gradually filling the trench as plants grow.\n\n## The Three-Year Wait\nDo not harvest in the first year. In the second year, take only a few spears per plant for no longer than two weeks. From the third year onwards, cut spears over a 6–8 week season.\n\n## Summer Care\nAfter the harvest season, allow the ferns to grow freely — they manufacture the energy reserves that next year's crop depends on. Do not cut them down until they yellow in autumn.`,
    harvesting: `Cut when spears are 12–18cm tall and the tips are still tight. Use a sharp knife to cut 5cm below the soil surface. Stop harvesting after 6–8 weeks from the first cut — leaving later spears to grow protects the plant's energy reserves.`,
    storage: 'Asparagus begins losing its sweetness from the moment it is cut. Eat on the day of harvest, or store upright in a jug of cold water in the fridge for no more than 2 days.',
    pests: ['Asparagus beetle', 'Slugs', 'Aphids'],
    diseases: ['Asparagus rust', 'Violet root rot', 'Fusarium wilt'],
    organicTips: [
      'Pick off asparagus beetle adults and larvae by hand — they are easy to spot',
      'Apply a generous mulch of compost each autumn to feed the crowns',
      'Do not let weeds establish — their roots compete with and can damage asparagus crowns',
      'Male plants produce more and better spears than female ones — choose all-male varieties',
    ],
    historicalNote: 'Asparagus was prized in ancient Rome — the Emperor Augustus is said to have coined the phrase "velocius quam asparagi conquantur" (faster than you can cook asparagus) to mean "immediately." In the 19th century, the asparagus season was a major event in fashionable London, with early forced crops fetching extraordinary prices at market.',
    nutritionFacts: 'Rich in folate, vitamins K and C, and asparagine (an amino acid that gives asparagus its diuretic properties). The antioxidant glutathione is present in particularly high concentrations.',
    imageUrl: '/images/plants/asparagus.jpg',
    tags: ['perennial', 'spring', 'luxury', 'long-term', 'root'],
  },

  {
    id: 'rhubarb',
    name: 'Rhubarb',
    latinName: 'Rheum × hybridum',
    family: 'leafy',
    emoji: '🌸',
    description: 'Rhubarb is the most faithful crop in the garden — planted once, it returns year after year for decades. The tart, vivid red stems make the finest crumbles, jams, and fools. Force it in late winter by covering the crowns and you can harvest the first pink stems while snow may still be on the ground.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'transplant',
    calendar: {
      transplant: [11, 12, 3, 4],
      harvest: [4, 5, 6, 7],
    },
    companions: ['brassica', 'onion'],
    antagonists: [],
    soilType: 'Rich, deeply-cultivated, moisture-retentive loam. Well-rotted manure dug in generously.',
    soilPH: { min: 6.0, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 90, plant: 90 },
    depthCm: 5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 365, max: 730 },
    cultivation: `## Planting\nBuy dormant crowns (offsets) rather than growing from seed. Plant in winter with the buds just visible above the soil surface, 90cm apart. Do not harvest in the first year — allow the plant to establish.\n\n## Forcing\nTo harvest early pink stems in late January–February, cover dormant crowns with an upturned dustbin or terracotta forcer. The forced stems will be pale pink and more tender. Remove the forcer after 6 weeks and allow the plant to recover.\n\n## Annual Feeding\nTop-dress with a generous mulch of well-rotted manure each autumn. Rhubarb is a hungry plant and poor soil means spindly, sour stems.`,
    harvesting: `Pull stems with a firm downward twist — do not cut, as a cut stump can rot. Take no more than half the stems from each plant in a season. The leaves are poisonous (containing oxalic acid) — compost them or boil to make an organic aphid spray.`,
    storage: 'Rhubarb is best cooked and frozen. Chopped raw stems can be frozen directly — no blanching needed. Cooked rhubarb keeps in the fridge for a week.',
    pests: ['Slugs', 'Crown rot (more a disease than pest)'],
    diseases: ['Crown rot', 'Honey fungus', 'Downy mildew'],
    organicTips: [
      'Mulch deeply with manure each autumn — rhubarb is a heavy feeder',
      'Boil rhubarb leaves in water to make a spray against aphids (use with care)',
      'Divide crowns every 5–6 years when productivity declines',
      'Never harvest all the stems — leave at least 4–5 leaves to sustain the plant',
    ],
    historicalNote: 'Rhubarb has been grown in China for over 2,000 years as a medicine (for its laxative properties) before arriving in European gardens. The "Rhubarb Triangle" in West Yorkshire — famous for forced rhubarb grown by candlelight in dark sheds — supplied Victorian and Edwardian England with early stems and retains Protected Designation of Origin status.',
    nutritionFacts: 'Rich in vitamin K and calcium (though the oxalic acid in rhubarb can inhibit calcium absorption). Very low in calories.',
    imageUrl: '/images/plants/rhubarb.jpg',
    tags: ['perennial', 'spring', 'fruit', 'preserve', 'easy', 'long-term'],
  },

  {
    id: 'sweetcorn',
    name: 'Sweetcorn',
    latinName: 'Zea mays',
    family: 'leafy',
    emoji: '🌽',
    description: 'The race between the pot of boiling water and the freshly-picked cob of sweetcorn is one of the garden\'s great moments. Sweetcorn begins converting its sugars to starch the moment it is picked — grow your own and you will wonder why you ever bought it from a supermarket.',
    difficulty: 'moderate',
    biodynamicDay: 'fruit',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5],
      transplant: [5, 6],
      harvest: [8, 9, 10],
    },
    companions: ['courgette', 'bean', 'squash', 'cucumber'],
    antagonists: ['fennel', 'tomato'],
    soilType: 'Well-drained, fertile soil. Warm up the soil with cloches before planting.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 45, plant: 35 },
    depthCm: 3,
    daysToGerminate: { min: 10, max: 14 },
    daysToHarvest: { min: 70, max: 90 },
    cultivation: `## Block Planting\nSweetcorn is wind-pollinated — for the best cob-set, plant in a block rather than rows. Pollen falls from the tassels above on to the silks below; in a single row, much pollen is lost to the wind.\n\n## Sowing\nSow under glass for frost protection in April or May, or direct into warm soil from late May. Plant 3cm deep.\n\n## Support\nSweetcorn stalks are tall and can blow over in strong winds. Earth up around the base once 60cm tall to provide additional stability.`,
    harvesting: `Test for ripeness by pulling back a little of the husk and pressing a fingernail into a kernel — if a milky white liquid is released, the cob is ripe. If clear, it is too early; if doughy, it is overripe. Pick and cook immediately.`,
    storage: 'Eat on the day of picking. For storage, blanch and freeze within hours. Sweetcorn deteriorates faster than almost any other vegetable.',
    pests: ['Slugs (on seedlings)', 'Aphids', 'Fruit fly', 'Birds (when germinating)'],
    diseases: ['Smut (Ustilago maydis)', 'Northern corn leaf blight'],
    organicTips: [
      'Block-plant for good wind pollination — a square block beats a straight row',
      'The "Three Sisters" combination — sweetcorn with climbing beans and courgettes — is one of the great organic growing systems',
      'Sow in toilet roll tubes to avoid root disturbance when transplanting',
      'Cook within minutes of harvest for the sweetest flavour',
    ],
    imageUrl: '/images/plants/sweetcorn.jpg',
    tags: ['warm-season', 'summer', 'block-plant', 'fresh', 'three-sisters'],
  },

  {
    id: 'aubergine',
    name: 'Aubergine',
    latinName: 'Solanum melongena',
    family: 'solanaceae',
    emoji: '🍆',
    description: 'With their glossy, jewel-like fruit in shades of deep purple, white, and striped, aubergines are among the most ornamental vegetables you can grow. In the British climate they are best in a greenhouse or polytunnel, though a very warm, sheltered spot outdoors can work in a good summer.',
    difficulty: 'challenging',
    biodynamicDay: 'fruit',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [2, 3, 4],
      transplant: [5, 6],
      harvest: [8, 9, 10],
    },
    companions: ['bean', 'pepper', 'tomato'],
    antagonists: ['fennel'],
    soilType: 'Rich, free-draining soil with generous organic matter. Best in containers or grow-bags in a greenhouse.',
    soilPH: { min: 5.5, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 60, plant: 50 },
    depthCm: 0.5,
    daysToGerminate: { min: 10, max: 21 },
    daysToHarvest: { min: 80, max: 100 },
    cultivation: `## Heat Requirements\nAubergines need warmth at every stage. Sow in a heated propagator at 21–24°C from February to April. Grow on in the warmest possible position — a heated greenhouse is ideal, though an unheated polytunnel works well in most British summers.\n\n## Pinching Out\nPinch out the growing tip when plants are 30–40cm tall to encourage bushy growth. Support with a cane. Limit each plant to 5–6 fruits for the best size and quality.\n\n## Feeding\nFeed weekly with a high-potash organic liquid feed once the first flowers open.`,
    harvesting: `Harvest when the skin is glossy and taut — a dull skin indicates the fruit is past its best and the flavour will be bitter. Cut with a sharp knife leaving 3cm of stalk attached.`,
    storage: 'Store at room temperature — refrigeration impairs the texture. Use within a week of harvest. Sliced aubergine can be frozen after salting and briefly frying.',
    pests: ['Red spider mite', 'Aphids', 'Whitefly'],
    diseases: ['Botrytis', 'Verticillium wilt', 'Powdery mildew'],
    organicTips: [
      'Introduce Phytoseiulus persimilis as a biological control for red spider mite',
      'Maintain high humidity in the greenhouse to deter red spider mite',
      'Limit each plant to 5 fruits — quality improves dramatically with thinning',
      'Grow in a grow-bag in a greenhouse rather than open ground for easier temperature control',
    ],
    imageUrl: '/images/plants/aubergine.jpg',
    tags: ['solanaceae', 'warm-season', 'greenhouse', 'challenging', 'mediterranean'],
  },

  {
    id: 'pepper',
    name: 'Pepper & Chilli',
    latinName: 'Capsicum annuum',
    family: 'solanaceae',
    emoji: '🌶️',
    description: 'From sweet bell peppers to incendiary chillies, Capsicum annuum encompasses one of the most varied vegetable species in cultivation. Grow them for a splash of colour in the garden and a world of flavour in the kitchen. The hotter you grow them, the hotter the chillies.',
    difficulty: 'moderate',
    biodynamicDay: 'fruit',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [2, 3, 4],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['tomato', 'basil', 'carrot', 'parsley'],
    antagonists: ['fennel', 'apricot'],
    soilType: 'Free-draining, sandy or loamy soil. Avoid cold, wet ground.',
    soilPH: { min: 6.0, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 50, plant: 45 },
    depthCm: 0.5,
    daysToGerminate: { min: 10, max: 21 },
    daysToHarvest: { min: 70, max: 90 },
    cultivation: `## Sowing\nSow into trays in early spring at 18–24°C, then transplant to gradually larger pots as plants grow — they resent root restriction. Treat and feed in the same way as tomatoes.\n\n## Heat and Chilli\nChilli peppers produce more capsaicin (the compound responsible for heat) when grown in warm, slightly stressful conditions. Allow the compost to dry slightly between waterings, and grow in the sunniest possible spot.\n\n## Outdoor Growing\nIn a long, warm British summer, peppers can be grown outdoors in a warm, sheltered, south-facing position. Otherwise, a greenhouse or polytunnel is strongly recommended.`,
    harvesting: `Sweet peppers can be harvested green (immature) or left to ripen to red, yellow, or orange — fully ripe peppers are sweeter but take longer. Chillies can be picked at any stage; they intensify in heat and flavour as they ripen. Use scissors to cut rather than pulling.`,
    storage: 'Store fresh peppers in the fridge for up to 2 weeks. Chillies can be dried by hanging in bunches in a warm, dry place — they will keep for a year. Both can be frozen.',
    pests: ['Aphids', 'Red spider mite', 'Whitefly'],
    diseases: ['Botrytis', 'Mosaic virus', 'Root rot'],
    organicTips: [
      'Allow compost to dry slightly between waterings for hotter chillies',
      'Feed weekly with a high-potash feed once flowers appear',
      'Hang dried chillies in bunches for decoration and use through winter',
      'Grow in pots that can be brought indoors in autumn to extend the season',
    ],
    nutritionFacts: 'Sweet peppers are among the richest sources of vitamin C — a red pepper contains three times more than an orange. Capsaicin in chillies has analgesic and anti-inflammatory properties.',
    imageUrl: '/images/plants/sweet-pepper.jpg',
    tags: ['solanaceae', 'warm-season', 'greenhouse', 'chilli', 'container'],
  },

  {
    id: 'celery',
    name: 'Celery',
    latinName: 'Apium graveolens',
    family: 'leafy',
    emoji: '🌿',
    description: 'Celery is one of the three great stock vegetables — celery, carrot, and onion form the aromatic base of countless soups, stews, and stocks. It is not the easiest crop to grow, preferring a rich, moist soil and cool conditions, but the flavour of home-grown celery is in a different league from anything sold in plastic bags.',
    difficulty: 'challenging',
    biodynamicDay: 'leaf',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4, 5],
      transplant: [5, 6],
      harvest: [9, 10, 11, 12],
    },
    companions: ['leek', 'onion', 'brassica', 'tomato'],
    antagonists: ['carrot', 'parsnip'],
    soilType: 'Very rich, moisture-retentive, deeply-cultivated soil. Add generous organic matter.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 30, plant: 25 },
    depthCm: 0.5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 120, max: 180 },
    cultivation: `## Self-Blanching vs Trench Celery\nSelf-blanching varieties are easier — plant in a block so outer plants shade inner ones. Traditional trench celery is earthed up progressively to blanch the stems and produce the finest, whitest sticks, but requires considerably more work.\n\n## Sowing\nSow thinly under glass in early spring. Celery seed is tiny and needs light to germinate — do not cover with compost. Prick out when large enough to handle and harden off before planting out after all frosts.\n\n## Watering\nCelery demands consistent moisture — drought stress causes bolting and bitter stalks. Never allow the soil to dry out.`,
    harvesting: `Lift the whole plant when the stems are thick enough to use. Leave self-blanching varieties in the ground until needed, but protect from hard frosts. Once lifted, trim roots and remove any damaged outer stems.`,
    storage: 'Keep in the ground until needed. Once cut, wrap in a damp cloth in the fridge for up to 2 weeks.',
    pests: ['Celery fly (leaf miner)', 'Slugs', 'Aphids', 'Carrot fly'],
    diseases: ['Celery leaf spot', 'Botrytis', 'Crown rot'],
    organicTips: [
      'Keep consistently moist — irregular watering is the most common cause of failure',
      'Net with insect mesh to exclude celery fly from the outset',
      'Self-blanching varieties are easier for most gardens and still produce good flavour',
    ],
    imageUrl: '/images/plants/celery.jpg',
    tags: ['leafy', 'autumn', 'stock-vegetable', 'challenging', 'long-season'],
  },

  // ── Herbs ──────────────────────────────────────────────────────────────────

  {
    id: 'chives',
    name: 'Chives',
    latinName: 'Allium schoenoprasum',
    family: 'herb',
    emoji: '🌿',
    description: 'Perhaps the easiest herb in the garden — chives are hardy, perennial, and practically indestructible. With their mild onion flavour, delicate purple flowers, and willingness to grow in almost any condition, they earn their space at the front of every herb bed.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5],
      harvest: [4, 5, 6, 7, 8, 9, 10],
    },
    companions: ['carrot', 'rose', 'apple', 'tomato'],
    antagonists: ['bean', 'pea'],
    soilType: 'Most soils tolerated. Prefer fertile, moist but well-drained ground.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 15 },
    depthCm: 1,
    daysToGerminate: { min: 10, max: 14 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Growing\nSow from seed in spring or divide established clumps. Chives will self-seed readily once established. Plant in the front of a border or in containers — they are tidy enough for ornamental planting.\n\n## Cutting Back\nCut the entire plant to within 5cm of the ground two or three times during the growing season. This rejuvenates the plant and produces fresh, bright growth. Freeze cut leaves for winter use.\n\n## Dividing\nDivide established clumps in autumn every 3–4 years to maintain vigour. Replant divisions generously spaced — they spread readily.`,
    harvesting: `Cut leaves as required with scissors, taking from the outside of the clump. The flowers are also edible and make a beautiful garnish. Freeze cut leaves by spreading on a tray, then storing in a bag — they crumble to a fine herb straight from frozen.`,
    storage: 'Freeze for winter use. Dried chives lose much of their flavour — freezing is far superior.',
    pests: ['Aphids', 'Onion fly'],
    diseases: ['Downy mildew', 'White rot'],
    organicTips: [
      'Plant around roses as a traditional organic aphid deterrent',
      'Grow alongside carrots to help confuse carrot fly',
      'Allow a few plants to flower each year — bees and hoverflies love them',
      'Chop and freeze in ice cubes for winter cooking',
    ],
    tags: ['herb', 'allium', 'perennial', 'edible-flower', 'easy', 'container'],
  },

  {
    id: 'dill',
    name: 'Dill',
    latinName: 'Anethum graveolens',
    family: 'herb',
    emoji: '🌿',
    description: 'With its feathery, blue-green foliage and soft anise flavour, dill is indispensable in the herb garden. Both the leaves and seeds are used in cooking — the leaves in salads, fish dishes, and tzatziki; the seeds in pickling brines and bread. Do not grow near fennel or the two will cross-pollinate.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [4, 5, 6, 7],
      harvest: [6, 7, 8, 9],
    },
    companions: ['cabbage', 'cucumber', 'lettuce', 'onion'],
    antagonists: ['fennel', 'carrot', 'tomato'],
    soilType: 'Well-drained, sandy or loamy soil in a sheltered position.',
    soilPH: { min: 5.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 20, plant: 10 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 40, max: 70 },
    cultivation: `## Sowing\nSow direct in shallow rows from late spring to late summer. Do not transplant — dill has a taproot and resents disturbance. Sow successionally every 4–6 weeks for a continuous supply of fresh leaves.\n\n## Isolation from Fennel\nDill and fennel will cross-pollinate if grown near each other. Keep them on opposite sides of the garden.\n\n## Harvesting Leaves vs Seeds\nFor maximum leaf production, pinch out the developing flower heads to delay bolting. For seeds, allow the plants to flower and set seed fully.`,
    harvesting: `Cut leafy stems as needed. Harvest seeds when the flower heads are fully brown and dry — cut the whole head and dry upside down in a paper bag to catch the seeds.`,
    storage: 'Fresh leaves can be frozen. Dried dill seeds store in a sealed jar for up to a year.',
    pests: ['Aphids', 'Carrot fly'],
    diseases: ['Downy mildew'],
    organicTips: [
      'Plant dill to attract beneficial insects — hoverflies and lacewings love the flowers',
      'Sow successionally every 4 weeks for continuous leaf production',
      'Keep well away from fennel to prevent cross-pollination',
    ],
    imageUrl: '/images/plants/dill.jpg',
    tags: ['herb', 'annual', 'easy', 'attract-beneficial-insects', 'edible-seeds'],
  },

  // ── NEW PLANTS ────────────────────────────────────────────────────────────────

  {
    id: 'broccoli',
    name: 'Broccoli',
    latinName: 'Brassica oleracea var. italica',
    family: 'brassica',
    emoji: '🥦',
    description: 'Purple and white sprouting broccoli are among the most rewarding winter vegetables — hardy, productive, and ready to harvest in the hungry gap of late winter and early spring when the garden has little else to offer.',
    difficulty: 'moderate',
    biodynamicDay: 'flower',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4],
      sowOutdoors: [5, 6, 7],
      transplant: [6, 7],
      harvest: [1, 2, 3, 4],
    },
    companions: ['onion', 'celery', 'beetroot', 'chard'],
    antagonists: ['tomato', 'pepper', 'strawberry', 'runner-bean'],
    soilType: 'Firm, fertile soil with good drainage. Firm soil prevents wind-rock in tall plants.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 60, plant: 60 },
    depthCm: 1,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 220, max: 280 },
    cultivation: `## Purple Sprouting Broccoli\nThis is the classic overwinter brassica of the British kitchen garden. Sow in spring, transplant after midsummer, and harvest side shoots from February to April — right when the garden is at its emptiest. It's a long commitment but enormously rewarding.\n\n## Calabrese\nThe green 'tree broccoli' sold in supermarkets is actually calabrese (Brassica oleracea var. italica), a summer crop sown from April to June and harvested 10–12 weeks later. It does not overwinter.\n\n## Club Root Prevention\nLime the soil to pH 7.0–7.5 before planting if club root is a risk. Rotate brassicas rigorously. Firm transplants in well to minimise air pockets.`,
    harvesting: `Cut the side shoots when they are firm and tightly budded, before the yellow flowers open. Harvest the central spear first to encourage a long succession of side shoots over 4–6 weeks. Pick every few days — regular harvesting is key to prolonging the season.`,
    storage: 'Use as fresh as possible. Will keep for a few days in the fridge. Best steamed or stir-fried — never boil.',
    pests: ['Cabbage white butterfly caterpillars', 'Cabbage root fly', 'Aphids', 'Slugs (on young plants)', 'Pigeons'],
    diseases: ['Club root (Plasmodiophora brassicae)', 'Powdery mildew', 'Downy mildew', 'White blister'],
    organicTips: [
      'Cover with fine insect-proof netting from planting to exclude cabbage white butterflies',
      'Place cabbage root fly collars around the base of transplants',
      'Firm soil thoroughly around transplants to help anchor plants against winter winds',
      'Lime the soil to help suppress club root — a pH above 7 is inhospitable to the spores',
      'Grow from fresh seed each year — bought transplants can carry club root into a clean bed',
    ],
    historicalNote: 'Purple sprouting broccoli was one of the mainstays of the traditional British kitchen garden, prized for its hardiness and its ability to fill the late winter hungry gap. Calabrese — the green broccoli of the supermarkets — is an Italian variety that became dominant in Britain only in the late 20th century.',
    nutritionFacts: 'One of the most nutrient-dense vegetables: exceptionally high in vitamins C and K, folate, iron, and cancer-fighting glucosinolates. Eating it raw or lightly steamed preserves the most nutrition.',
    imageUrl: '/images/plants/broccoli.jpg',
    tags: ['brassica', 'winter-hardy', 'hungry-gap', 'long-season', 'flower'],
  },

  {
    id: 'fennel',
    name: 'Florence Fennel',
    latinName: 'Foeniculum vulgare var. azoricum',
    family: 'root',
    emoji: '🌿',
    description: 'Florence fennel produces a swollen, crisp, anise-flavoured bulb at the base of its feathery stems. Raw in salads or braised in butter, it brings a Mediterranean elegance to the kitchen. Notoriously prone to bolting — choose modern bolt-resistant varieties and sow from midsummer.',
    difficulty: 'moderate',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [6, 7, 8],
      harvest: [8, 9, 10],
    },
    companions: ['dill', 'cabbage'],
    antagonists: ['tomato', 'bean-runner', 'carrot', 'coriander'],
    soilType: 'Light, well-drained, moderately fertile soil. Heavy or waterlogged soil causes bolting.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 20 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Bolting\nFlorence fennel's main challenge is its tendency to bolt (run to seed) before the bulb develops. The key is to sow from June to August — never in spring — and to choose modern bolt-resistant varieties like 'Zefa Fino' or 'Romanesco'.\n\n## Sowing\nSow direct in shallow rows (fennel dislikes transplanting). Thin to 20–25cm apart. Direct sowing avoids root disturbance that can trigger bolting.\n\n## Earthing Up\nWhen bulbs reach the size of a golf ball, earth up the base with soil to blanch them — this whitens the bulb and improves the texture and flavour.`,
    harvesting: `Cut at soil level when bulbs are swollen and firm — roughly the size of a tennis ball. Do this before cold autumn nights set in. Leave the roots in the ground: they will re-sprout with ferny foliage useful as a herb.`,
    storage: 'Best eaten fresh. Will keep for 4–5 days wrapped in the fridge. The feathery fronds wilt quickly — use immediately.',
    pests: ['Aphids', 'Slugs', 'Carrot fly'],
    diseases: ['Fusarium rot', 'Powdery mildew', 'Downy mildew'],
    organicTips: [
      'Sow in July for the most bolt-resistant results — long days and cold nights trigger bolting in spring sowings',
      'Earth up bulbs to blanch them as they develop',
      'Do not transplant — sow direct to avoid root disturbance',
      'Keep fennel away from dill or the two will cross-pollinate and produce inferior seed',
      'Keep well watered during dry spells — water stress triggers bolting',
    ],
    historicalNote: 'Florence fennel originates from the Mediterranean and has been cultivated since Roman times. In Britain it remained a specialist crop until recently, associated with Italian cooking. The herb fennel (Foeniculum vulgare) — grown for leaves and seeds — is a different, hardier perennial form of the same species.',
    nutritionFacts: 'Good source of fibre, vitamin C, potassium, and manganese. The anise compound anethole has antioxidant and anti-inflammatory properties.',
    imageUrl: '/images/plants/florence-fennel.jpg',
    tags: ['root', 'mediterranean', 'summer', 'bulb', 'moderate', 'bolt-prone'],
  },

  {
    id: 'artichoke',
    name: 'Globe Artichoke',
    latinName: 'Cynara cardunculus var. scolymus',
    family: 'flower',
    emoji: '🌿',
    description: 'The globe artichoke is one of the most architectural plants in the kitchen garden — towering, silver-leaved, and magnificent in flower if you let it go to seed. The edible part is the immature flower head, eaten before the petals open. Perennial, productive, and impressively beautiful.',
    difficulty: 'moderate',
    biodynamicDay: 'flower',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4],
      transplant: [5],
      harvest: [6, 7, 8],
    },
    companions: ['tarragon', 'sunflower', 'borage'],
    antagonists: [],
    soilType: 'Deep, rich, well-drained soil. Drought-tolerant once established but needs fertile conditions.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 90, plant: 90 },
    depthCm: 1,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 365, max: 540 },
    cultivation: `## Long-Term Investment\nGlobe artichokes are perennial and live for 4–6 years before declining. Year one produces little harvest — by years two and three they are at peak productivity. Give them permanent, well-prepared beds.\n\n## Propagation\nThe easiest propagation is from offsets — rooted side-shoots taken from an established plant in spring. These establish quickly and crop reliably. Growing from seed produces variable plants.\n\n## Winter Protection\nIn colder areas (north of the Midlands), protect the crowns over winter with a thick mulch of straw or bracken. In mild areas they are surprisingly tough.`,
    harvesting: `Cut the central head first when it is large, firm, and tightly closed — before any purple colour shows between the scales. Side heads follow over the next 4–6 weeks. Always harvest before the flower opens, otherwise the heart becomes tough.`,
    storage: 'Use within a few days. Can be stored upright in a jug of water in the fridge for up to a week.',
    pests: ['Blackfly (aphids)', 'Slugs', 'Earwigs'],
    diseases: ['Petal blight', 'Botrytis grey mould', 'Crown rot'],
    organicTips: [
      'Mulch heavily in spring with well-rotted compost — artichokes are hungry plants',
      'Snap off unwanted side shoots in spring to direct energy to the main crown',
      'Protect crowns from hard frost with straw mulch in autumn',
      'Divide and replant from offsets every 4–5 years to renew vigour',
    ],
    historicalNote: 'Globe artichokes have been cultivated in the Mediterranean for at least 2,000 years. They were a prized delicacy in Renaissance Europe and were introduced to Britain by Flemish settlers in the 16th century. Catherine de\' Medici is said to have been extremely fond of them.',
    nutritionFacts: 'High in fibre, folate, vitamin C, vitamin K, and magnesium. Also contain cynarin, which stimulates bile production and aids digestion. One of the highest-antioxidant vegetables.',
    imageUrl: '/images/plants/artichoke.jpg',
    tags: ['perennial', 'flower', 'architectural', 'summer', 'long-term', 'mediterranean'],
  },

  {
    id: 'jerusalem-artichoke',
    name: 'Jerusalem Artichoke',
    latinName: 'Helianthus tuberosus',
    family: 'root',
    emoji: '🌻',
    description: 'Jerusalem artichokes are vigorous, statuesque plants with edible knobbly tubers that taste like a cross between potato and artichoke heart. They are among the easiest vegetables to grow and virtually indestructible — though notorious for the digestive effects of their inulin content.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [2, 3, 4],
      harvest: [10, 11, 12, 1],
    },
    companions: ['nasturtium', 'sunflower'],
    antagonists: ['potato'],
    soilType: 'Any soil, including poor or heavy soils. Will grow where almost nothing else will.',
    soilPH: { min: 5.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 60, plant: 30 },
    depthCm: 10,
    daysToGerminate: { min: 14, max: 28 },
    daysToHarvest: { min: 180, max: 210 },
    cultivation: `## Vigour Warning\nJerusalem artichokes grow to 2–3 metres tall and can become invasive if not managed. Grow them in a permanent dedicated bed or use them as a windbreak hedge. Always harvest every last tuber or the patch will spread relentlessly.\n\n## Planting\nPlant egg-sized tubers in late winter or early spring, 10–15cm deep. Growth emerges in spring and the plants reach full height by midsummer, topped with cheerful yellow sunflower-like flowers.\n\n## Frost Sweetening\nTubers are best harvested after the first frosts, which convert some of the inulin to fructose, improving flavour and reducing digestive discomfort.`,
    harvesting: `Wait until after the first frost for best flavour. Cut down the tall stems to about 30cm, then dig the entire patch with a fork, extracting every tuber you can find. Leave some small tubers for next year's crop if continuing.`,
    storage: 'Leave in the ground and harvest as needed through winter — they store best in the soil. Lifted tubers store for a few weeks in a cool, dark place.',
    pests: ['Slugs', 'Sclerotinia (white mould)'],
    diseases: ['Sclerotinia rot', 'Rhizoctonia'],
    organicTips: [
      'Plant in a permanent bed or large container to prevent them spreading',
      'Harvest every tuber at the end of the season — any left will regenerate',
      'Leave tubers in the ground and harvest fresh through winter',
      'Do not grow near potatoes as both compete aggressively',
      'The tall stems make an excellent temporary windbreak',
    ],
    historicalNote: 'Despite the name, Jerusalem artichokes are neither from Jerusalem nor artichokes. They are native to North America and were introduced to Europe in the early 17th century. The name may derive from the Italian "girasole" (sunflower). They were popular as a starvation food in wartime and fell out of fashion until the recent interest in heritage vegetables.',
    nutritionFacts: 'Very high in inulin (a prebiotic fibre that feeds beneficial gut bacteria) and iron. The inulin content explains their notorious flatulence-inducing effect — it is fermented by gut bacteria. Also contain thiamine and potassium.',
    imageUrl: '/images/plants/jerusalem-artichoke.jpg',
    tags: ['root', 'perennial', 'winter', 'easy', 'windbreak', 'unusual'],
  },

  {
    id: 'celeriac',
    name: 'Celeriac',
    latinName: 'Apium graveolens var. rapaceum',
    family: 'root',
    emoji: '🥬',
    description: 'Celeriac is the celery of the root cellar — all the rich, earthy flavour of celery packed into a dense, knobbly root that stores through the entire winter. A demanding crop that rewards patience and attention with exceptional results.',
    difficulty: 'challenging',
    biodynamicDay: 'root',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [2, 3, 4],
      transplant: [5, 6],
      harvest: [10, 11, 12, 1],
    },
    companions: ['leek', 'onion', 'bean-runner'],
    antagonists: ['carrot', 'parsnip'],
    soilType: 'Deep, rich, moisture-retentive soil. Celeriac needs consistent moisture throughout the growing season.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 40, plant: 30 },
    depthCm: 0.5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 180, max: 220 },
    cultivation: `## Starting Early\nCeleriac needs a very long growing season. Sow under glass from February to April in warmth (15–18°C). Prick out individually. Do not rush transplanting — plants need 6–8 true leaves and night temperatures above 10°C before going out, or they may bolt.\n\n## Earthing Up and Side Shoot Removal\nAs the swollen crown develops above soil level, remove the lower leaves and any side shoots regularly. This directs energy into the root and produces a cleaner, more usable swelling.\n\n## Watering\nCeleriac is very susceptible to bolting and hollow crowns if water is inconsistent. Mulch heavily to retain moisture and water regularly in dry spells.`,
    harvesting: `Lift from October onwards, after light frosts have improved flavour. Use a fork to loosen the soil around the root — the knobby root breaks if pulled directly. Trim off the leaf stalks and root fibres. Can be left in the ground through mild winters and harvested as needed.`,
    storage: 'Store in boxes of barely moist sand in a frost-free shed for up to 6 months. Or leave in the ground and lift as needed through winter.',
    pests: ['Celery fly (leaf miner)', 'Carrot fly', 'Slugs', 'Aphids'],
    diseases: ['Leaf spot (Septoria)', 'Botrytis', 'Sclerotinia rot', 'Heart rot'],
    organicTips: [
      'Start in heat no later than early April to ensure a long enough growing season',
      'Remove lower leaves and side shoots regularly as the root develops to direct energy',
      'Mulch heavily to retain consistent moisture — drought causes bolting and hollow roots',
      'Cover with fleece in autumn to extend the harvest season',
    ],
    historicalNote: 'Celeriac was developed in Northern and Central Europe from wild celery (smallage) sometime in the 16th–17th century, as a root-forming alternative better suited to northern winters. It is far more widely eaten on the Continent than in Britain, where it has only recently gained wider recognition.',
    nutritionFacts: 'Good source of vitamin K, phosphorus, vitamin C, and fibre. Lower in calories than potato. Contains the compound phthalide, which is associated with lowering blood pressure.',
    imageUrl: '/images/plants/celeriac.jpg',
    tags: ['root', 'winter', 'long-season', 'store', 'challenging', 'continental'],
  },

  {
    id: 'kohlrabi',
    name: 'Kohlrabi',
    latinName: 'Brassica oleracea var. gongylodes',
    family: 'brassica',
    emoji: '🥬',
    description: 'Kohlrabi grows a swollen, spherical stem just above ground level, with a mild, sweet flavour somewhere between turnip, cabbage, and apple. It is fast-growing, versatile (good raw in slaws, roasted, or stir-fried), and comes in striking green or purple forms.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [2, 3],
      sowOutdoors: [3, 4, 5, 6, 7],
      transplant: [4, 5],
      harvest: [5, 6, 7, 8, 9, 10],
    },
    companions: ['onion', 'beetroot', 'cucumber'],
    antagonists: ['tomato', 'fennel', 'runner-bean'],
    soilType: 'Fertile, well-drained soil. Less demanding than most brassicas — tolerates poor soil better.',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 20 },
    depthCm: 1,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 50, max: 70 },
    cultivation: `## Fast and Simple\nKohlrabi is one of the least demanding brassicas and one of the fastest to mature. Sow in succession from early spring to midsummer for a continuous harvest. It handles both light frosts and summer heat well.\n\n## Succession Sowing\nSow small batches every 3–4 weeks from March to July to avoid a glut. Kohlrabi turns woody and pithy if left too long in the ground after reaching full size.\n\n## Varieties\nGreen varieties are faster-maturing and better for summer. Purple varieties are hardier and good for autumn growing. The flesh inside is always pale and similar in both.`,
    harvesting: `Harvest when the swollen stem is 5–8cm in diameter — about the size of a tennis ball. Do not leave any larger or the flesh becomes woody. Twist off the leaves and trim the root.`,
    storage: 'Remove the leaves and store in a cool, dark place or fridge for up to 3 weeks. Will last in the ground for a few weeks without deteriorating in cool weather.',
    pests: ['Cabbage white butterfly caterpillars', 'Aphids', 'Flea beetle (small holes in leaves)', 'Slugs'],
    diseases: ['Club root', 'Downy mildew', 'Black rot'],
    organicTips: [
      'Cover with fine mesh to exclude flea beetle, which is particularly damaging to young seedlings',
      'Net against cabbage white butterflies — the caterpillars destroy brassica leaves quickly',
      'Rotate with non-brassicas each year to prevent club root build-up',
      'Eat the leaves — young kohlrabi tops are nutritious and taste like cabbage',
    ],
    historicalNote: 'Kohlrabi was developed from wild cabbage in Northern Europe and has been grown since at least the 16th century. It is a staple vegetable in Germany, Austria, and Eastern Europe but remains something of a curiosity in Britain and America, despite recent interest from gardeners and chefs.',
    nutritionFacts: 'High in vitamin C, fibre, and potassium. The leaves are particularly rich in vitamin K and calcium.',
    imageUrl: '/images/plants/kohlrabi.jpg',
    tags: ['brassica', 'root', 'fast-growing', 'cool-season', 'succession', 'easy'],
  },

  {
    id: 'chinese-cabbage',
    name: 'Chinese Cabbage',
    latinName: 'Brassica rapa subsp. pekinensis',
    family: 'brassica',
    emoji: '🥬',
    description: 'Chinese cabbage — also called Napa cabbage or Chinese leaf — forms a large, elongated, crisp head with crinkled pale-green leaves and a mild, delicate flavour. Excellent raw in salads, in stir-fries, and for kimchi. Fast-growing but prone to bolting in spring.',
    difficulty: 'moderate',
    biodynamicDay: 'leaf',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [7, 8],
      sowOutdoors: [7, 8],
      transplant: [8],
      harvest: [9, 10, 11],
    },
    companions: ['dill', 'garlic', 'spinach'],
    antagonists: ['strawberry', 'tomato'],
    soilType: 'Rich, moisture-retentive, well-drained soil. Requires consistent moisture.',
    soilPH: { min: 6.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 45, plant: 35 },
    depthCm: 1,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 60, max: 80 },
    cultivation: `## Avoid Bolting\nChinese cabbage is highly sensitive to day length and low temperatures — spring sowings almost always bolt before heading up. The secret is to sow from late July to August for autumn harvest, when decreasing day length and cooler temperatures promote hearting rather than flowering.\n\n## Sowing\nSow in cell trays or direct in shallow rows. Handle with care when transplanting as root disturbance can trigger bolting. For direct sowing, thin to 30–35cm.\n\n## Watering\nConsistent moisture is essential for good head formation. Irregular watering causes tip burn (brown leaf edges) and poor hearts.`,
    harvesting: `Cut at the base when the head feels firm and well-filled. Harvest before hard frost. Outer leaves can be removed as needed — the inner heart will remain fresh for longer.`,
    storage: 'Will keep in the fridge for 2–3 weeks. Can be stored in a cool shed for several weeks in autumn.',
    pests: ['Flea beetle', 'Cabbage white butterfly caterpillars', 'Slugs', 'Aphids'],
    diseases: ['Club root', 'Downy mildew', 'Soft rot'],
    organicTips: [
      'Sow only from late July onwards to avoid bolting — this is the single most important rule',
      'Cover with fine netting to protect from flea beetle and cabbage white butterfly',
      'Water consistently to prevent tip burn on the inner leaves',
      'Harden off carefully before transplanting — chill shock can trigger bolting',
    ],
    historicalNote: 'Chinese cabbage has been cultivated in East Asia for over 1,500 years. It came to Europe in the 20th century and gained popularity in British kitchens through Chinese restaurant culture. It is now one of the most grown brassicas worldwide.',
    nutritionFacts: 'High in vitamin C, vitamin K, folate, and calcium. Very low in calories. Contains glucosinolates associated with cancer prevention.',
    imageUrl: '/images/plants/chinese-cabbage.jpg',
    tags: ['brassica', 'leaf', 'cool-season', 'autumn', 'quick-crop', 'salad'],
  },

  {
    id: 'swede',
    name: 'Swede',
    latinName: 'Brassica napus var. napobrassica',
    family: 'brassica',
    emoji: '🥬',
    description: 'The swede is the great winter root vegetable of the British table — sweet, dense, and deeply comforting. Roasted or mashed, it becomes something wonderful. It stores in the ground through severe frost and is one of the most dependable crops for winter eating.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [5, 6, 7],
      harvest: [10, 11, 12, 1, 2],
    },
    companions: ['onion', 'leek', 'pea'],
    antagonists: ['turnip'],
    soilType: 'Any well-drained soil. Tolerates poorer conditions than most vegetables.',
    soilPH: { min: 5.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 40, plant: 23 },
    depthCm: 1,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 150, max: 180 },
    cultivation: `## Direct Sowing\nSow thinly in shallow rows in late spring to midsummer. Thin in stages to 23cm. Swedes need the long days of summer to develop but must be established by midsummer.\n\n## Thinning\nThin progressively to final spacing. Thinnings are edible as a leaf vegetable. The final plants should have about 23cm between them.\n\n## Powder Mildew\nPowdery mildew can be a problem in late summer. Improve air circulation by keeping plants well spaced. Resistant modern varieties like 'Marian' avoid many traditional problems.`,
    harvesting: `Harvest from October onwards. Swedes are frost-hardy and actually improve in flavour after a frost (which converts starches to sugars). They can stay in the ground until needed through winter, though in very waterlogged conditions it is safer to lift and store.`,
    storage: 'Leave in the ground and harvest as needed. Lifted swedes store for several months in a cool, frost-free place.',
    pests: ['Flea beetle', 'Cabbage root fly', 'Slugs'],
    diseases: ['Club root', 'Powdery mildew', 'Brown heart (boron deficiency)'],
    organicTips: [
      'Apply boron (in the form of borax) if brown heart is a recurring problem in your soil',
      'Lime the soil to pH 7 to suppress club root',
      'Grow resistant varieties like "Marian" or "Best of All"',
      'Leave in the ground: frost improves flavour by converting starches to sugar',
    ],
    historicalNote: 'The swede (known as "rutabaga" in North America) was developed as a cross between turnip and wild cabbage in Bohemia around the 17th century. In Britain it became a staple of the northern table — Scots in particular have a strong tradition of eating "neeps" mashed with butter alongside haggis on Burns Night.',
    nutritionFacts: 'Good source of vitamin C, potassium, and fibre. Lower in carbohydrates than potato. Contains glucosinolates with potential anti-cancer properties.',
    imageUrl: '/images/plants/swede.jpg',
    tags: ['root', 'winter', 'brassica', 'store', 'easy', 'frost-hardy'],
  },

  {
    id: 'turnip',
    name: 'Turnip',
    latinName: 'Brassica rapa subsp. rapa',
    family: 'brassica',
    emoji: '🥬',
    description: 'Turnips are among the fastest-maturing root vegetables — pulled young, they are sweet, crisp, and delicious raw or lightly cooked. Left to grow larger they develop a stronger, earthier flavour perfect for winter soups and stews. The leafy tops (turnip greens) are also excellent eating.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 7, 8],
      harvest: [5, 6, 7, 9, 10, 11],
    },
    companions: ['pea', 'spinach'],
    antagonists: ['swede', 'kale'],
    soilType: 'Firm, well-drained, not too rich. Avoid freshly manured ground.',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 13 },
    depthCm: 1,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 40, max: 70 },
    cultivation: `## Two Season Crop\nTurnips suit two growing seasons well: early spring for young, sweet baby turnips harvested in May and June; and late summer for maincrop autumn roots. Avoid midsummer sowing — heat and drought cause woody, bitter roots.\n\n## Succession Sowing\nSow in short rows every 2–3 weeks from March through May and again from July. This prevents a glut and ensures a steady supply of young, tender roots.\n\n## Baby Turnips\nPulled at golf-ball size (4–5cm), turnips are a revelation — crisp, mildly peppery, and completely unlike their overgrown counterparts. Eat raw in salads, or flash-roasted whole.`,
    harvesting: `For baby turnips, harvest at 4–5cm diameter. For maincrop, harvest from October onwards. Twist off the tops (leave some greens for cooking). Do not leave in the ground too long after maturity or they become pithy and hollow.`,
    storage: 'Remove leaves and store in boxes of sand or in a cool, dark place for several months.',
    pests: ['Flea beetle', 'Cabbage white butterfly caterpillars', 'Cabbage root fly', 'Slugs'],
    diseases: ['Club root', 'Powdery mildew', 'Turnip mosaic virus'],
    organicTips: [
      'Cover with fine netting from sowing to exclude flea beetle — the first enemy of turnip seedlings',
      'Rotate with non-brassicas to prevent club root build-up',
      'Harvest baby turnips young for the best flavour and texture',
      'Cook the leafy tops — they are nutritious and delicious stir-fried with garlic',
    ],
    historicalNote: 'Turnips were a staple vegetable across Northern Europe for centuries before the potato arrived from the New World. They fed both people and livestock through the winter and played a vital role in the agricultural revolution as a fodder crop enabling year-round livestock feeding.',
    nutritionFacts: 'Good source of vitamin C, fibre, and glucosinolates. The leafy tops are rich in vitamins A, C, K, and folate — more nutritious than the root itself.',
    imageUrl: '/images/plants/turnip.jpg',
    tags: ['root', 'brassica', 'quick-crop', 'succession', 'easy', 'cool-season'],
  },

  {
    id: 'swiss-chard',
    name: 'Swiss Chard',
    latinName: 'Beta vulgaris subsp. cicla',
    family: 'leafy',
    emoji: '🌿',
    description: 'Swiss chard — especially the rainbow-coloured "Rainbow Chard" — is one of the most beautiful and productive vegetables in the garden. The large leaves with vivid scarlet, yellow, orange, pink, and white stems can be harvested continuously for months. Stems and leaves are cooked separately; both are delicious.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [3, 4],
      sowOutdoors: [4, 5, 6, 7],
      transplant: [5, 6],
      harvest: [6, 7, 8, 9, 10, 11, 12],
    },
    companions: ['onion', 'garlic', 'cabbage', 'bean-runner'],
    antagonists: [],
    soilType: 'Rich, fertile, moisture-retentive soil. Tolerant of heavier soils than most vegetables.',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 35, plant: 30 },
    depthCm: 2,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 60, max: 80 },
    cultivation: `## Cut and Come Again\nSwiss chard is the quintessential cut-and-come-again vegetable. Remove outer leaves regularly, leaving the central growing point intact, and the plant will produce continuously for months.\n\n## Two Crops\nSpring sowings (April–May) provide summer and autumn harvest. A second sowing in late June or July provides a young crop to overwinter and harvest heavily in spring before it bolts.\n\n## Overwintering\nHardy varieties like 'Perpetual Spinach' and standard Swiss chard will overwinter in all but the harshest winters. Cover with fleece during severe frosts. They are often productive until the following summer.`,
    harvesting: `Remove individual outer leaves with a sharp knife, cutting cleanly at the base of the stalk. Never strip the plant bare — always leave at least 4 inner leaves to regenerate. Stems and leaves cook at different rates — separate them and add stems a few minutes earlier.`,
    storage: 'Best eaten fresh. Will keep in the fridge for 3–4 days. The stems can be stored separately for slightly longer.',
    pests: ['Slugs', 'Aphids', 'Leaf miners (creating pale tunnels in leaves)'],
    diseases: ['Downy mildew', 'Powdery mildew', 'Crown rot in waterlogged soil'],
    organicTips: [
      'Sow a second batch in summer for a spring harvest from overwintered plants',
      'Remove yellowed or damaged outer leaves promptly to keep the plant productive',
      'Separate stems from leaves when cooking — add stems first as they take longer',
      'Rainbow Chard is as ornamental as it is edible — use it in flower borders',
    ],
    historicalNote: 'Swiss chard is closely related to the beetroot and has been grown in the Mediterranean since antiquity. Despite its name, it is not particularly Swiss — the name is used to distinguish it from French chard in English-language seed catalogues. Its relative "perpetual spinach" (a form of chard) was a staple overwintering vegetable in British gardens before frozen spinach arrived.',
    nutritionFacts: 'Excellent source of vitamins K, A, and C. Also contains magnesium, potassium, iron, and fibre. Rich in antioxidants and the pigment betalain.',
    imageUrl: '/images/plants/swiss-chard.jpg',
    tags: ['leafy', 'cut-and-come-again', 'ornamental', 'easy', 'year-round', 'colourful'],
  },

  {
    id: 'marrow',
    name: 'Marrow',
    latinName: 'Cucurbita pepo',
    family: 'cucurbit',
    emoji: '🥒',
    description: 'The marrow is the courgette grown to its full, generous size — tender when young and harvested early, but left to mature it develops into a large, striped vegetable perfect for stuffing and baking. Once a staple of the British allotment, marrows are enjoying a revival among kitchen gardeners.',
    difficulty: 'easy',
    biodynamicDay: 'fruit',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['bean-runner', 'pea', 'nasturtium', 'sweetcorn'],
    antagonists: ['potato', 'fennel'],
    soilType: 'Very rich, moisture-retentive soil. Ideally planted over a compost pit or on a mound of well-rotted manure.',
    soilPH: { min: 5.5, max: 6.8 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 120, plant: 90 },
    depthCm: 2.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Courgette vs Marrow\nA marrow is simply a courgette (Cucurbita pepo) allowed to grow to full size. Any courgette variety can be grown on to produce marrows, but dedicated marrow varieties have tougher skins suited to storage. Traditional varieties include 'Long Green Trailing' and 'Badminton'.\n\n## Growing On\nCulture is identical to courgette. Sow on edge in individual pots in warmth, transplant after the last frost, and grow in a sunny, sheltered spot with rich, fertile soil.\n\n## Pollination\nFor mature marrows, ensure good pollination. Hand-pollinate if necessary by transferring pollen from male flowers (thin stalk, no embryo fruit) to female flowers (with miniature marrow at the base) using a fine brush.`,
    harvesting: `For tender 'baby marrows', harvest at 20cm. For full-size marrows for storing, allow to reach 30–45cm and leave until the skin is hard enough to resist thumbnail pressure. Cut with 5–8cm of stalk attached to discourage rot. Tap the ripe marrow and listen for a hollow sound.`,
    storage: 'Mature marrows with hard skins store for up to 6 months hanging in a net bag in a cool, frost-free shed. Check regularly for soft spots.',
    pests: ['Slugs (on young plants)', 'Aphids', 'Vine weevil', 'Red spider mite'],
    diseases: ['Powdery mildew', 'Cucumber mosaic virus', 'Botrytis'],
    organicTips: [
      'Grow on a mound over a pit of well-rotted manure for the richest possible growing medium',
      'Hand-pollinate in cool summers using a fine paintbrush',
      'Spray foliage with diluted milk to prevent powdery mildew',
      'Harvest regularly — even one overlooked marrow diverts energy from the whole plant',
    ],
    historicalNote: 'The marrow was the archetypal British allotment vegetable for generations — the subject of competitions, the symbol of the self-sufficient smallholder. Before the courgette arrived from the Mediterranean in the 1970s, the marrow was how British people ate this plant. The courgette is simply a marrow harvested young.',
    nutritionFacts: 'Low in calories, high in water content. Contains vitamin C, potassium, and manganese. The seeds are edible when roasted.',
    imageUrl: '/images/plants/marrow.jpg',
    tags: ['cucurbit', 'summer', 'allotment', 'store', 'stuffing', 'british-classic'],
  },

  {
    id: 'squash',
    name: 'Winter Squash',
    latinName: 'Cucurbita maxima',
    family: 'cucurbit',
    emoji: '🎃',
    description: 'Winter squashes — including butternut, Crown Prince, and Hubbard — are among the most rewarding crops for flavour and storage. Unlike courgettes and summer squash, they are harvested in autumn with a hard skin and stored through winter, bringing sweetness and colour to the table for months.',
    difficulty: 'moderate',
    biodynamicDay: 'fruit',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [4, 5],
      sowOutdoors: [5, 6],
      transplant: [5, 6],
      harvest: [9, 10],
    },
    companions: ['bean-runner', 'pea', 'sweetcorn', 'nasturtium'],
    antagonists: ['potato', 'fennel'],
    soilType: 'Very rich, deep, moisture-retentive soil. Squash are hungry, thirsty plants needing lots of organic matter.',
    soilPH: { min: 5.5, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'high',
    spacingCm: { row: 150, plant: 90 },
    depthCm: 2.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 90, max: 120 },
    cultivation: `## Long Season\nWinter squash need a long growing season — at least 12 weeks from planting to harvest. Sow indoors in April or May, and transplant after the last frost. In the UK, space is required and a warm summer is needed for butternut types to ripen fully.\n\n## Training\nThe trailing vines can sprawl over large areas. Peg down vines at nodes to encourage additional rooting and stability. Alternatively, train vertically up a strong trellis, supporting individual fruits in netting bags as they develop.\n\n## Curing\nAfter harvest, cure squashes by leaving them in a warm, sunny spot for 10–14 days. This hardens the skin and dramatically extends storage life.`,
    harvesting: `Harvest when the skin is hard and the stalk begins to dry and corky. Most winter squash are ready from late September to early October. Always harvest before the first frost. Cut with 5–8cm of stalk attached to prevent rot at the attachment point.`,
    storage: 'After curing, store in a dry, cool (10–15°C) room. Properly cured squash will store for 3–6 months. Check regularly and use any that show soft spots immediately.',
    pests: ['Vine weevil', 'Slugs', 'Aphids', 'Red spider mite'],
    diseases: ['Powdery mildew', 'Cucumber mosaic virus', 'Phytophthora root rot'],
    organicTips: [
      'Cure harvested squashes in sun for 2 weeks to dramatically extend storage life',
      'Train vines vertically to save space and improve air circulation',
      'Prune off excess side shoots to direct energy into developing fruits',
      'Limit to 3–4 fruits per plant for the best size and quality',
    ],
    historicalNote: 'Winter squash originated in Central America and has been cultivated for at least 8,000 years — among the oldest cultivated crops in the Americas. They spread to Europe after the 16th century but remained curiosities until the 21st-century interest in heritage and seasonal vegetables.',
    nutritionFacts: 'Exceptional source of beta-carotene (vitamin A precursor), vitamin C, potassium, and fibre. The bright orange-fleshed varieties are particularly rich in antioxidants.',
    imageUrl: '/images/plants/squash.jpg',
    tags: ['cucurbit', 'warm-season', 'store', 'winter', 'long-season', 'butternut'],
  },

  {
    id: 'rocket',
    name: 'Rocket',
    latinName: 'Eruca vesicaria subsp. sativa',
    family: 'leafy',
    emoji: '🥗',
    description: 'Rocket is the fastest-growing salad leaf in the kitchen garden — from sowing to first harvest in as little as four weeks. Its peppery, pungent leaves give character to salads and wilted into pasta sauces are exceptional. A few consecutive sowings provides an endless supply through the growing season.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 6, 7, 8, 9],
      harvest: [4, 5, 6, 7, 8, 9, 10, 11],
    },
    companions: ['lettuce', 'spinach', 'radish'],
    antagonists: [],
    soilType: 'Any reasonably fertile, well-drained soil. Tolerant and undemanding.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 15, plant: 10 },
    depthCm: 0.5,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 25, max: 40 },
    cultivation: `## Quick and Simple\nRocket is one of the easiest and fastest salad crops. Scatter seed thinly in rows or broadcast over a patch, water in, and harvest in 4–6 weeks. It needs very little attention.\n\n## Succession Sowing\nRocket bolts quickly in heat, producing tough, very pungent leaves and flowers. The flowers are also edible and peppery. Sow small amounts every 2–3 weeks from March to September for a continuous supply of tender young leaves.\n\n## Wild Rocket\nWild rocket (Diplotaxis tenuifolia) is a perennial species with a stronger, more intensely peppery flavour. It is slower-growing but more enduring, coming back year after year. Grow it alongside annual rocket for different strengths of flavour.`,
    harvesting: `Cut when leaves are 5–10cm long, leaving a 2cm stump to regrow. Alternatively, harvest the whole plant and resow. Pick from the outside of the plant, leaving the central growing point. Best picked young — older leaves become very hot and tough.`,
    storage: 'Eat fresh. Rocket wilts rapidly after picking. Keep in a sealed bag in the fridge for 1–2 days maximum.',
    pests: ['Flea beetle (small round holes in leaves)', 'Slugs', 'Aphids'],
    diseases: ['Downy mildew', 'Clubroot'],
    organicTips: [
      'Cover with fine netting to exclude flea beetle, which devastates young rocket seedlings',
      'Sow in partial shade in midsummer to slow bolting',
      'Sow successionally every 3 weeks for continuous harvest — individual sowings bolt quickly in summer',
      'Sow thinly: overcrowding reduces airflow and increases disease risk',
    ],
    historicalNote: 'Rocket has been eaten in the Mediterranean since Roman times. In Britain it was once an obscure herb found mainly in Italian delicatessens. Its explosion into mainstream popularity followed the rise of the "rocket, parmesan, and pine nut" salad in the restaurant culture of the 1990s. It is now one of the most commonly grown salad leaves in Britain.',
    nutritionFacts: 'High in vitamin K, vitamin A, vitamin C, folate, and calcium. Rich in glucosinolates (the compounds that give it its peppery flavour) which are associated with cancer prevention.',
    imageUrl: '/images/plants/rocket.jpg',
    tags: ['leafy', 'salad', 'quick-crop', 'succession', 'easy', 'peppery'],
  },

  {
    id: 'parsley',
    name: 'Parsley',
    latinName: 'Petroselinum crispum',
    family: 'herb',
    emoji: '🌿',
    description: 'Parsley is the indispensable kitchen herb — curly or flat-leaved (the latter preferred by cooks for its stronger flavour), it is used in virtually every cuisine. It is biennial, producing leaves in its first year and going to seed in its second. Famously slow to germinate, but worth every bit of patience.',
    difficulty: 'moderate',
    biodynamicDay: 'leaf',
    sowingMethod: 'both',
    calendar: {
      sowIndoors: [2, 3, 4],
      sowOutdoors: [4, 5, 6, 7],
      transplant: [4, 5],
      harvest: [5, 6, 7, 8, 9, 10, 11],
    },
    companions: ['tomato', 'asparagus', 'carrot', 'chives'],
    antagonists: ['mint'],
    soilType: 'Rich, well-drained, fertile soil with consistent moisture.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 30, plant: 20 },
    depthCm: 0.5,
    daysToGerminate: { min: 14, max: 28 },
    daysToHarvest: { min: 70, max: 90 },
    cultivation: `## Germination\nParsley is notoriously slow to germinate — up to 4–6 weeks. Pouring boiling water along the drill before sowing is said to improve germination by breaking down the seed coat. Keep the seedbed consistently moist and do not give up — germination can be erratic.\n\n## Varieties\nFlat-leaf (Italian) parsley has a stronger, more complex flavour preferred by chefs. Curly parsley is hardier and more decorative. Hamburg parsley (var. tuberosum) is grown for its edible root as well as leaves.\n\n## Biennial Nature\nParsley is biennial — it grows leaves in year one and sets seed in year two. For continuous supply, sow two batches: one in spring for the current year and one in summer for next year's harvest.`,
    harvesting: `Cut stems from the outside of the plant, leaving the inner leaves to continue growing. Never strip the plant completely. Harvest regularly to prevent the plant from bolting. Flat-leaf parsley is ready earlier than curly.`,
    storage: 'Stand in a glass of water like a bunch of flowers for up to a week. Or freeze whole leaves on a tray, then bag them — they shatter into usable pieces.',
    pests: ['Carrot fly', 'Aphids', 'Celery fly (leaf miner)'],
    diseases: ['Parsley leaf spot', 'Damping off (on seedlings)'],
    organicTips: [
      'Pour boiling water along the seed drill before sowing to speed up germination',
      'Cover with fleece or a cloche to extend the harvest into winter',
      'Sow a second batch in July to provide young plants for the following year',
      'Grow near tomatoes — claimed to improve fruit flavour and repel aphids',
    ],
    historicalNote: 'Parsley was associated with death in ancient Greek and Roman culture — used to decorate tombs and feed to funeral horses. It was believed that the seed visited the underworld nine times before germinating (hence the long wait). In medieval Britain it was used medicinally before becoming a universal culinary herb.',
    nutritionFacts: 'Exceptionally nutritious: one of the highest plant sources of vitamin K and very high in vitamins A and C. Also contains iron and folate. The fresh herb provides far more nutrients than the dried form.',
    imageUrl: '/images/plants/parsley.jpg',
    tags: ['herb', 'biennial', 'kitchen-staple', 'slow-germinator', 'moderate', 'aromatic'],
  },

  {
    id: 'chervil',
    name: 'Chervil',
    latinName: 'Anthriscus cerefolium',
    family: 'herb',
    emoji: '🌿',
    description: 'Chervil is the most delicate of the culinary herbs — feathery, pale green, with a subtle anise flavour that vanishes with heat. It is one of the four fines herbes of French cuisine. Unlike most herbs, chervil prefers cool weather and shade, making it invaluable in spring and autumn.',
    difficulty: 'easy',
    biodynamicDay: 'leaf',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 8, 9],
      harvest: [4, 5, 6, 9, 10, 11],
    },
    companions: ['lettuce', 'radish', 'carrot'],
    antagonists: [],
    soilType: 'Moist, fertile soil. Tolerates partial shade — prefers it in summer.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'partial',
    waterNeeds: 'moderate',
    spacingCm: { row: 20, plant: 15 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 35, max: 50 },
    cultivation: `## Cool-Season Herb\nChervil's greatest value is in spring and autumn, when it thrives in cool weather. It bolts quickly in summer heat. For summer use, sow under trees or in the shade of taller plants. For winter use, sow in late summer in a cold frame or unheated greenhouse.\n\n## Direct Sowing\nChervil resents transplanting — always sow direct where it is to grow. Scatter seed thinly in rows or broadcast. Do not cover deeply; the seed needs light to germinate.\n\n## Succession Sowing\nSow every 3–4 weeks from March to September for a continuous supply. Each batch provides 4–6 weeks of harvest before bolting.`,
    harvesting: `Cut individual fronds when 8–10cm long, or cut the whole plant to 3cm above the base for regrowth. Harvest before flowers appear — after bolting the leaves become bitter. Use immediately: chervil wilts within hours of cutting and loses its delicate flavour.`,
    storage: 'Chervil does not dry or freeze well — the volatile oils that carry the flavour are destroyed. Use fresh only. It can be chopped and stirred into softened butter, then frozen.',
    pests: ['Aphids', 'Slugs (on young seedlings)'],
    diseases: ['Powdery mildew'],
    organicTips: [
      'Grow in partial shade to slow bolting in summer',
      'Sow in autumn for a winter harvest under a cold frame',
      'Allow a few plants to self-seed each year — chervil self-sows reliably in a friendly spot',
      'Never cook chervil — add to dishes off the heat to preserve its subtle flavour',
    ],
    historicalNote: 'Chervil was used extensively in Roman cuisine and remained important in European cooking through the medieval period. It is now primarily associated with French cuisine. In Britain it was common in kitchen gardens until the 20th century but fell from use as French cuisine declined in popularity. It is one of the herbs experiencing a renaissance with the current interest in heritage vegetables and French country cooking.',
    nutritionFacts: 'Good source of vitamin C, iron, magnesium, and carotenoids. Contains volatile oils (including estragole and anethole) responsible for its characteristic flavour.',
    tags: ['herb', 'cool-season', 'annual', 'french-cuisine', 'shade-tolerant', 'quick-crop'],
  },

  {
    id: 'salsify',
    name: 'Salsify',
    latinName: 'Tragopogon porrifolius',
    family: 'root',
    emoji: '🌿',
    description: 'Salsify is an almost-forgotten root vegetable — long, white-skinned, and cylindrical, with a delicate flavour sometimes compared to oyster or artichoke. Once common in Victorian kitchen gardens, it is now a genuine rarity, which is a shame: it is easy to grow, keeps in the ground all winter, and tastes extraordinary roasted.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [4, 5],
      harvest: [10, 11, 12, 1, 2, 3],
    },
    companions: ['onion', 'carrot'],
    antagonists: [],
    soilType: 'Deep, light, stone-free soil. Avoid freshly manured ground (causes forking). Sandy loam ideal.',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 30, plant: 10 },
    depthCm: 1.5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 150, max: 180 },
    cultivation: `## Simple Culture\nSalsify is one of the less demanding root vegetables. Sow directly in spring in deep, light soil, thin to 10cm, and leave to develop through summer. It needs little attention.\n\n## Deep Soil Requirement\nThe long tapered roots — up to 30cm — need deep, loose, stone-free soil. If your soil is heavy, grow in raised beds or sow in holes filled with sandy compost.\n\n## Spring Shoots\nIf you leave salsify roots in the ground, they will produce edible young shoots in spring that can be blanched and eaten as a vegetable — these are considered a delicacy in their own right.`,
    harvesting: `Lift carefully with a fork from October onwards, as the roots are brittle and snap easily. Harvest as needed through winter — they improve in flavour after frost. In spring, harvest any remaining roots before they regrow.`,
    storage: 'Leave in the ground and harvest as needed. Lifted roots store for several weeks in a cool, dark place.',
    pests: ['Slugs', 'Carrot fly'],
    diseases: ['White blister', 'Powdery mildew'],
    organicTips: [
      'Grow in deep, light soil or raised beds for long, straight roots',
      'Leave in the ground through winter — frost improves the flavour',
      'Harvest the spring shoots from second-year plants — they are considered a delicacy',
      'A few plants allowed to flower produce striking purple blooms that attract pollinators',
    ],
    historicalNote: 'Salsify was grown extensively in Victorian kitchen gardens and appears frequently in 18th and 19th century cookery books. The old name "vegetable oyster" reflects its unusual delicate flavour. It fell from cultivation as the range of vegetables available commercially expanded and memories of wartime vegetable growing faded. It is now a rare heritage crop.',
    nutritionFacts: 'Good source of inulin (prebiotic fibre), potassium, and vitamin C. Also contains iron and calcium.',
    imageUrl: '/images/plants/salsify.jpg',
    tags: ['root', 'heritage', 'winter', 'unusual', 'easy', 'victorian'],
  },

  {
    id: 'scorzonera',
    name: 'Scorzonera',
    latinName: 'Scorzonera hispanica',
    family: 'root',
    emoji: '🌿',
    description: 'Scorzonera is the black-skinned sister of salsify — equally neglected, equally delicious. The long, cylindrical roots have a black or dark brown skin and white flesh with a mild, subtly sweet flavour that becomes more complex after frost. The white sap is mildly bitter. A wonderful heritage root vegetable.',
    difficulty: 'easy',
    biodynamicDay: 'root',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [4, 5],
      harvest: [10, 11, 12, 1, 2, 3],
    },
    companions: ['onion', 'carrot', 'leek'],
    antagonists: [],
    soilType: 'Deep, light, stone-free soil. Sandy loam ideal — the same requirements as salsify and parsnip.',
    soilPH: { min: 6.0, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 30, plant: 10 },
    depthCm: 1.5,
    daysToGerminate: { min: 14, max: 21 },
    daysToHarvest: { min: 150, max: 180 },
    cultivation: `## Similar to Salsify\nScorzonera is grown in exactly the same way as salsify. The key differences are the black skin, a slightly more robust flavour, and the fact that scorzonera can be left in the ground for a second year to develop even larger roots.\n\n## Second-Year Roots\nUnlike most vegetables, scorzonera improves if left for a second growing season. The roots become larger and more intensely flavoured. Roots too small to use in autumn can be left to develop further over the winter and following spring.\n\n## Preparation\nPeel after cooking (the skin comes off easily once blanched) to avoid the sticky white sap, which can stain hands and utensils. Or wear gloves and peel before cooking.`,
    harvesting: `Lift carefully with a fork from October onwards — the roots are long and brittle. Leave small roots in the ground to develop further. Frost improves flavour. In spring, harvest remaining roots or allow to re-shoot for young edible tops.`,
    storage: 'Leave in the ground through winter. Lifted roots store for a few weeks in a cool place.',
    pests: ['Slugs', 'Carrot fly'],
    diseases: ['White blister', 'Powdery mildew'],
    organicTips: [
      'Leave small roots in the ground to develop into second-year roots — they become much larger',
      'Peel scorzonera after briefly blanching in boiling water to avoid the sticky white sap',
      'Frost improves the flavour significantly — don\'t harvest before the first frosts if you can wait',
      'Allow a few plants to flower — the yellow dandelion-like flowers are beautiful',
    ],
    historicalNote: 'Scorzonera originates from southern Europe and Asia and has been cultivated since the 16th century. In Spain it was used as a remedy against snakebite — the name may derive from the Spanish "escuerzo" (toad) or the Catalan "escurçó" (viper). Like salsify, it was common in Victorian kitchen gardens but has almost completely vanished from British cultivation.',
    nutritionFacts: 'Good source of inulin (prebiotic fibre), iron, potassium, and zinc. Contains saponins which may lower cholesterol.',
    imageUrl: '/images/plants/scorzonera.jpg',
    tags: ['root', 'heritage', 'winter', 'unusual', 'easy', 'spanish'],
  },

  {
    id: 'coriander',
    name: 'Coriander',
    latinName: 'Coriandrum sativum',
    family: 'herb',
    emoji: '🌿',
    description: 'Coriander (known as cilantro in North America) is one of the most divisive herbs — deeply loved or strongly disliked in equal measure. The fresh leaves have an intensely aromatic, citrusy flavour used extensively in Asian, Middle Eastern, and South American cooking. The seeds are also an important spice.',
    difficulty: 'moderate',
    biodynamicDay: 'leaf',
    sowingMethod: 'direct',
    calendar: {
      sowOutdoors: [3, 4, 5, 8, 9],
      harvest: [5, 6, 7, 9, 10, 11],
    },
    companions: ['spinach', 'lettuce', 'chervil'],
    antagonists: ['fennel', 'dill'],
    soilType: 'Light, well-drained soil in a sheltered position. Dislikes heavy, waterlogged ground.',
    soilPH: { min: 6.0, max: 7.0 },
    sunlight: 'full',
    waterNeeds: 'low',
    spacingCm: { row: 20, plant: 10 },
    depthCm: 0.5,
    daysToGerminate: { min: 7, max: 14 },
    daysToHarvest: { min: 30, max: 50 },
    cultivation: `## The Bolting Problem\nCoriander's great weakness is its tendency to bolt (run to seed) rapidly in warm, dry weather. Spring sowings often bolt before producing usable leaves. The solution is to sow in cool weather — early spring or late summer — and to choose slow-bolt varieties.\n\n## For Leaves vs Seeds\nIf growing for leaves, pinch out the central growing tip to delay bolting and encourage bushy growth. If growing for seed (used as a spice), allow the plants to flower and seed freely.\n\n## Succession Sowing\nFor a continuous supply of leaves, sow every 3–4 weeks from early spring to early summer, and again from late summer to autumn. Each batch provides 3–4 weeks of leaf harvest before bolting.`,
    harvesting: `Cut young leafy stems when 10–15cm tall, leaving 3–4cm stump to regrow. Use immediately — coriander wilts very quickly after harvest. For seeds, harvest the entire plant when seeds turn brown and dry, and hang upside-down in a paper bag to catch falling seeds.`,
    storage: 'Fresh coriander keeps for 3–5 days with stems in water, loosely covered, at room temperature. Freeze whole leaves on a tray then bag. Seeds dry-store for 1–2 years.',
    pests: ['Aphids', 'Leaf miners', 'Slugs'],
    diseases: ['Powdery mildew', 'Damping off', 'Bacterial soft rot'],
    organicTips: [
      'Choose bolt-resistant varieties like "Confetti" or "Cilantro" for leaf production',
      'Sow in late summer for the best leaf harvest — cooler days slow bolting',
      'Grow in light shade during hot spells to extend the harvest period',
      'Allow some plants to set seed — they self-sow reliably in a friendly spot',
    ],
    historicalNote: 'Coriander is one of the world\'s oldest spices — seeds have been found in Egyptian tombs dating to 3000 BCE. It reached Britain with the Romans. As a leaf herb in British cooking it is relatively recent, introduced through immigration from South Asia and the Caribbean. Its popularity has surged enormously since the 1980s.',
    nutritionFacts: 'Leaves are high in vitamin K, vitamin A, and vitamin C. Contains volatile oils (linalool, geranyl acetate) responsible for the distinctive aroma. Seeds are high in dietary fibre and manganese.',
    imageUrl: '/images/plants/coriander.jpg',
    tags: ['herb', 'annual', 'aromatic', 'asian-cooking', 'bolt-prone', 'succession'],
  },

  {
    id: 'calabrese',
    name: 'Calabrese',
    latinName: 'Brassica oleracea var. italica',
    family: 'brassica',
    emoji: '🥦',
    description: 'Calabrese is the green broccoli of the supermarket — large, domed central heads followed by a succession of smaller side shoots. Faster and less demanding than purple sprouting broccoli, it is sown in spring and harvested from midsummer. Pick before any yellow appears in the florets for the best flavour.',
    difficulty: 'moderate',
    biodynamicDay: 'flower',
    sowingMethod: 'transplant',
    calendar: {
      sowIndoors: [3, 4, 5],
      sowOutdoors: [4, 5, 6],
      transplant: [5, 6],
      harvest: [7, 8, 9, 10],
    },
    companions: ['onion', 'celery', 'beetroot', 'potato'],
    antagonists: ['tomato', 'fennel', 'runner-bean', 'strawberry'],
    soilType: 'Firm, fertile, well-drained soil. Firm planting prevents wind-rock and improves head formation.',
    soilPH: { min: 6.5, max: 7.5 },
    sunlight: 'full',
    waterNeeds: 'moderate',
    spacingCm: { row: 45, plant: 30 },
    depthCm: 1,
    daysToGerminate: { min: 5, max: 10 },
    daysToHarvest: { min: 60, max: 90 },
    cultivation: `## Sowing\nSow indoors in cell trays from March to May, or direct outdoors from April onwards. Calabrese dislikes root disturbance, so cell-raised transplants are preferable to pricking out from seed trays. Transplant at the 4–5 true leaf stage.\n\n## Planting Out\nFirm transplants in well — loose planting leads to wind-rock, which disrupts root development and causes poor hearts. Space 30cm within rows and 45cm between rows for standard-sized heads. Closer spacing (25cm) produces smaller heads more suited to single-serving use.\n\n## Side Shoots\nAfter cutting the central head, leave the plant in the ground. It will produce a succession of smaller side shoots over the following 4–6 weeks, extending the harvest considerably. Feed with a nitrogen-rich liquid feed after the central head is cut to encourage vigorous side shoot development.`,
    harvesting: `Cut the central head when it is firm, domed, and tightly budded — before any yellow flower colour shows between the florets. Cut with a sharp knife at a slant to prevent water pooling on the stump. Harvest side shoots regularly once they reach 8–10cm, cutting just above a leaf joint.`,
    storage: 'Use as fresh as possible — calabrese deteriorates quickly after cutting. Will keep for 3–4 days in the fridge. Blanch and freeze surplus shoots for up to a year.',
    pests: ['Cabbage white butterfly caterpillars', 'Cabbage root fly', 'Mealy aphids', 'Slugs (on young plants)', 'Pigeons'],
    diseases: ['Club root (Plasmodiophora brassicae)', 'Downy mildew', 'White blister', 'Botrytis on cut stumps'],
    organicTips: [
      'Cover with fine insect-proof netting from transplanting to harvest to exclude cabbage white butterflies',
      'Use cabbage root fly collars around transplant stems — an old carpet underlay cut into 15cm squares works well',
      'Lime the soil before planting if club root is a risk — a pH above 7 discourages the spores',
      'Water consistently during dry spells — irregular moisture causes hollow stems',
      'Feed with liquid seaweed or comfrey tea after cutting the central head to encourage side shoots',
    ],
    historicalNote: 'Calabrese takes its name from Calabria, the toe of the Italian boot, where it has been grown for centuries. It reached Britain after the Second World War through Italian immigration and market gardening, and became mainstream in the 1980s when it began appearing in supermarkets. It is now one of the most widely eaten vegetables in Britain, though many people call it simply "broccoli" — the purple sprouting type, once the only broccoli in British gardens, is now the lesser-known variety.',
    nutritionFacts: 'One of the most nutritious vegetables: very high in vitamins C and K, folate, potassium, and fibre. Rich in sulforaphane and glucosinolates — compounds with well-documented anti-cancer properties. Best eaten lightly steamed or raw to preserve the sulforaphane content.',
    imageUrl: '/images/plants/calabrese.jpg',
    tags: ['brassica', 'summer', 'flower', 'popular', 'quick-crop', 'side-shoots'],
  },
];

export const getPlantById = (id: string): Plant | undefined =>
  PLANTS.find(p => p.id === id);

export const getPlantsByFamily = (family: string): Plant[] =>
  PLANTS.filter(p => p.family === family);

export const getPlantsByMonth = (month: number): Plant[] =>
  PLANTS.filter(p => {
    const c = p.calendar;
    return (
      c.sowIndoors?.includes(month) ||
      c.sowOutdoors?.includes(month) ||
      c.transplant?.includes(month) ||
      c.harvest?.includes(month)
    );
  });

export const PLANT_FAMILIES = [
  { id: 'allium',     label: 'Alliums',        emoji: '🧅' },
  { id: 'brassica',   label: 'Brassicas',       emoji: '🥦' },
  { id: 'cucurbit',   label: 'Cucurbits',       emoji: '🥒' },
  { id: 'legume',     label: 'Legumes',         emoji: '🫛' },
  { id: 'solanaceae', label: 'Solanums',        emoji: '🍅' },
  { id: 'root',       label: 'Root Vegetables', emoji: '🥕' },
  { id: 'leafy',      label: 'Leafy Greens',    emoji: '🥬' },
  { id: 'herb',       label: 'Herbs',           emoji: '🌿' },
  { id: 'fruit',      label: 'Fruit',           emoji: '🍓' },
  { id: 'flower',     label: 'Edible Flowers',  emoji: '🌸' },
];
